<template>
  <div>
    <!-- Skip Link para acessibilidade -->
    <a href="#main-content" class="skip-link sr-only-focusable">
      Pular para o conteúdo principal
    </a>

    <b-img fluid class="bkg_img" :src="require('@/assets/images/pages/graphic-1.png')" />
    <div id="portal-cebas-credentialing-public" :style="dynamicStyles">
      <!-- Main Content Landmark -->
      <main id="main-content" role="main" tabindex="-1">
       <b-card
          class="h-full"
          :class="primaryVariant ? `header-bg-${primaryVariant}` : 'header-bg-dynamic'"
          no-body>
          <template #header>
            <span class="text-white text-lg font-bold">{{ portalCebasConfig.title_page || 'Credenciamento CEBAS' }}</span>
          </template>
          <b-container
            fluid
            class="p-4"
            :class="primaryVariant ? `bg-${primaryVariant}` : 'bg-dynamic-primary'"
            v-if="!hasCebasCredentialing">
            <b-row>
              <b-col>
                <b-img center thumbnail fluid :src="require('@/assets/images/pages/404-2.png')" alt="Página 404"></b-img>
              </b-col>
            </b-row>
          </b-container>

          <!-- Formulário Step-by-Step -->
          <div class="pt-1 p-1" v-if="hasCebasCredentialing">

            <!-- Tela Inicial -->
            <div v-if="!formStarted" class="initial-screen">
              <div class="mb-4">
                <complex-editor-view :editor_data="infoPageContent" :read-only="true"/>
              </div>

              <b-card :class="primaryVariant ? `border-${primaryVariant}` : 'border-dynamic-primary'">
                <div class="text-center">
                  <h5 :class="primaryVariant ? `card-title text-${primaryVariant} mb-4` : 'card-title text-dynamic-primary mb-4'">
                    <i class="feather icon-info mr-2"></i>
                    Antes de Prosseguir
                  </h5>

                  <b-alert variant="info" show class="text-left mb-4">
                    <div class="alert-body">
                      <h6 class="alert-heading">
                        <i class="feather icon-alert-circle mr-1"></i>
                        Importante!
                      </h6>
                      <hr>
                      <p class="mb-2">
                        <strong>• Tempo estimado:</strong> 15-20 minutos para completar todo o processo
                      </p>
                      <p class="mb-2">
                        <strong>• Documentos necessários:</strong> Tenha em mãos todos os documentos em formato PDF
                      </p>
                      <p class="mb-2">
                        <strong>• Dados obrigatórios:</strong> Todos os campos indicados como obrigatórios devem ser preenchidos
                      </p>
                      <p class="mb-0">
                        <strong>• Salvamento:</strong> Você pode pausar e retomar o preenchimento a qualquer momento
                      </p>
                    </div>
                  </b-alert>

                  <div class="verification-section">
                    <b-form-group class="checkbox-wrapper">
                      <b-form-checkbox
                        v-model="initialAcceptance.dataVeracity"
                        :state="getInitialFieldState('dataVeracity')"
                        :class="!primaryVariant ? 'checkbox-dynamic-primary' : ''"
                        class="custom-checkbox-large"
                      >
                        <span class="checkbox-text">
                          Declaro que todas as informações que serão fornecidas durante este processo são
                          <strong :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'">verdadeiras e correspondem à realidade</strong>,
                          estando ciente de que a prestação de informações falsas constitui crime.
                        </span>
                      </b-form-checkbox>
                      <b-form-invalid-feedback>
                        Você deve confirmar a veracidade das informações para prosseguir
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group class="checkbox-wrapper mt-3">
                      <b-form-checkbox
                        v-model="initialAcceptance.dataCollection"
                        :state="getInitialFieldState('dataCollection')"
                        :class="!primaryVariant ? 'checkbox-dynamic-primary' : ''"
                        class="custom-checkbox-large"
                      >
                        <span class="checkbox-text">
                          Autorizo o <strong :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'">tratamento dos meus dados pessoais</strong>
                          conforme a Lei Geral de Proteção de Dados (LGPD), para fins de análise
                          desta solicitação de credenciamento.
                        </span>
                      </b-form-checkbox>
                      <b-form-invalid-feedback>
                        Você deve autorizar o tratamento dos dados para prosseguir
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>

                  <div class="action-buttons mt-4">
                    <div class="d-grid gap-4">
                      <!-- Botão principal: Iniciar -->
                      <b-button
                        :variant="primaryVariant"
                        :class="!primaryVariant ? 'btn-dynamic-primary' : ''"
                        size="lg"
                        @click="showStartCredentialingModal"
                        :disabled="!canStartProcess"
                        class="px-5"
                      >
                        <i class="feather icon-play mr-2"></i>
                        Iniciar Credenciamento
                      </b-button>

                      <!-- Separador visual -->
                      <div class="text-center">
                        <small class="text-muted">ou</small>
                      </div>

                      <!-- Botão secundário: Continuar -->
                      <b-button
                        variant="outline-primary"
                        :class="!primaryVariant ? 'btn-outline-dynamic-primary' : ''"
                        size="lg"
                        @click="showContinueCredentialingModal"
                        class="px-5"
                      >
                        <i class="feather icon-refresh-cw mr-2"></i>
                        Continuar Credenciamento
                      </b-button>
                    </div>

                    <p class="text-muted mt-3 mb-0">
                      <small>
                        <i class="feather icon-shield mr-1"></i>
                        Seus dados estão protegidos e serão utilizados apenas para este processo
                      </small>
                    </p>
                  </div>
                </div>
              </b-card>
            </div>

            <!-- Form Wizard (só aparece após iniciar) -->
            <div v-else class="wizard-container main-content-container">
              <!-- Botão para voltar à tela inicial -->
              <div class="d-flex justify-content-between align-items-center mb-3" ref="badgeContainer">
                <div class="d-flex align-items-center">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="backToInitialScreen"
                    class="mr-2"
                  >
                    <i class="feather icon-arrow-left mr-1"></i>
                    Voltar ao Início
                  </b-button>

                  <!-- Botão de Mock para Desenvolvimento -->
                  <b-button
                    v-if="isDevelopment"
                    variant="outline-warning"
                    size="sm"
                    @click="fillWithMockData"
                    class="mr-2"
                    title="Preencher com dados de teste (apenas em desenvolvimento)"
                  >
                    <i class="feather icon-zap mr-1"></i>
                    Mock Data
                  </b-button>

                  <!-- Botão de Teste do Computed infoPageContent -->
                  <b-button
                    v-if="isDevelopment"
                    variant="outline-info"
                    size="sm"
                    @click="testInfoPageBehavior"
                    class="mr-2"
                    title="Alternar entre conteúdo padrão e da API (apenas em desenvolvimento)"
                  >
                    <i class="feather icon-toggle-right mr-1"></i>
                    Info Page
                  </b-button>

                  <!-- Botão de Debug dos Dados para API -->
                  <b-button
                    v-if="isDevelopment"
                    variant="outline-secondary"
                    size="sm"
                    @click="debugFormData"
                    class="mr-2"
                    title="Ver dados que serão enviados para API (apenas em desenvolvimento)"
                  >
                    <i class="feather icon-code mr-1"></i>
                    Debug API
                  </b-button>

                  <!-- Toggle do Auto-Save -->
                  <b-button
                    v-if="isDevelopment"
                    :variant="autoSaveEnabled ? 'outline-success' : 'outline-danger'"
                    size="sm"
                    @click="toggleAutoSave"
                    class="mr-2"
                    :title="`Auto-save está ${autoSaveEnabled ? 'ativo' : 'inativo'} - clique para alternar`"
                  >
                    <i :class="autoSaveEnabled ? 'feather icon-check' : 'feather icon-x'" class="mr-1"></i>
                    Auto-Save
                  </b-button>
                </div>

                <div class="d-flex align-items-center">
                  <b-badge
                    :variant="formData.status && formData.status.color ? formData.status.color : 'primary'"
                    class="px-3 py-2 mr-2">
                    <i class="feather icon-clock mr-1"></i>
                    {{ formData.status && formData.status.label ? formData.status.label : 'Processo Iniciado' }}
                  </b-badge>

                  <!-- Badge de modo visualização -->
                  <b-badge
                    v-if="isFormDisabled"
                    variant="secondary"
                    class="px-2 py-1 mr-2"
                    title="Formulário em modo somente leitura - navegação permitida apenas para visualização"
                  >
                    <i class="feather icon-eye mr-1"></i>
                    Visualização
                  </b-badge>

                  <!-- Indicador de Salvamento Normal -->
                  <b-badge
                    v-if="isSaving && !floatingSavingBadge"
                    variant="warning"
                    class="px-2 py-1"
                    title="Salvando dados na API..."
                  >
                    <i class="feather icon-upload spinning mr-1"></i>
                    Salvando...
                  </b-badge>

                  <!-- Badge de Último Salvamento -->
                  <b-badge
                    v-else-if="lastSaveTime && !isFormDisabled"
                    variant="success"
                    class="px-2 py-1"
                    :title="`Último salvamento: ${formatLastSaveTime()}`"
                  >
                    <i class="feather icon-check mr-1"></i>
                    Salvo
                  </b-badge>
                </div>
              </div>

              <!-- Badge Flutuante de Salvamento -->
              <b-badge
                v-if="isSaving && floatingSavingBadge"
                class="floating-saving-badge px-2 py-1"
                title="Salvando dados na API..."
                :style="getFloatingBadgeStyle()"
              >
                <i class="feather icon-upload spinning mr-1"></i>
                Salvando...
              </b-badge>

              <form-wizard
                ref="credentialingWizard"
                :color="primaryColor"
                :title="null"
                :subtitle="null"
                :finish-button-text="finishButtonText"
                :next-button-text="nextButtonText"
                :back-button-text="backButtonText"
                :finish-button-disabled="isFormDisabled"
                class="steps-transparent mb-3"
                @on-complete="submitCredentialingForm"
                @on-change="handleStepChange"
                :enable-all-nav="!isFormDisabled"
              >                <!-- Passo 1: Dados Básicos -->
                <tab-content
                  :title="getStepTitle('basic_data')"
                  :icon="getStepIcon('basic_data')"
                  :before-change="validateBasicData"
                >
                  <basic-data-step
                    :form-data="formData.basicData"
                    :errors="getBasicDataErrors()"
                    :primary-variant="primaryVariant"
                    :disabled="isFormDisabled"
                    @update:form-data="updateBasicData"
                  />
                </tab-content>

                <!-- Passo 2: Documentação Jurídica -->
                <tab-content
                  title="Documentação Jurídica"
                  icon="feather icon-file-text"
                  :before-change="validateJuridicalDocumentation"
                >
                  <JuridicalDocumentationStep
                    ref="juridicalDocumentationStep"
                    :form-data="formData.juridicalDocumentation"
                    :errors="getJuridicalDocumentationErrors()"
                    :primary-variant="primaryVariant"
                    :disabled="isFormDisabled"
                    @update:form-data="updateJuridicalDocumentation"
                    @upload-document="handleUploadJuridicalDocumentation"
                    @remove-document="handleRemoveJuridicalDocumentation"
                  />
                </tab-content>

                <!-- Passo 3: Dados Econômicos e Operacionais -->
                <tab-content
                  title="Dados Econômicos e Operacionais"
                  icon="feather icon-bar-chart"
                  :before-change="validateEconomicOperationalData"
                >
                  <EconomicOperationalDataStep
                    :form-data="formData.economicOperationalData"
                    :errors="getEconomicOperationalDataErrors()"
                    :disabled="isFormDisabled"
                    @update:form-data="updateEconomicOperationalData"
                    @upload-document="handleUploadEconomicOperationalData"
                    @remove-document="handleRemoveEconomicOperationalData"
                  />
                </tab-content>

                <!-- Passo 4: Unidades Vinculadas -->
                <tab-content
                  title="Unidades Vinculadas"
                  icon="feather icon-link"
                  :before-change="validateLinkedInstitutions"
                >
                  <LinkedInstitutionsStep
                    ref="linkedInstitutionsStep"
                    :form-data="formData.linkedInstitutions"
                    :errors="getLinkedInstitutionsErrors()"
                    :disabled="isFormDisabled"
                    @update:form-data="updateLinkedInstitutions"
                    @add-institution="handleAddInstitution"
                    @remove-institution="handleRemoveInstitution"
                  />
                </tab-content>

                <!-- Passo 5: Anexos Complementares -->
                <tab-content
                  title="Anexos Complementares"
                  icon="feather icon-paperclip"
                  :before-change="validateAdditionalAttachments"
                >
                  <AdditionalAttachmentsStep
                    :form-data="formData.additionalAttachments"
                    :errors="getAdditionalAttachmentsErrors()"
                    :link="link"
                    :credentialing-id="credentialingId"
                    :disabled="isFormDisabled"
                    @update:form-data="updateAdditionalAttachments"
                    @upload-document="handleUploadAdditionalAttachments"
                    @remove-document="handleRemoveAdditionalAttachments"
                  />
                </tab-content>

                <!-- Passo 6: Revisão e Envio -->
                <tab-content
                  title="Revisão"
                  icon="feather icon-check-circle"
                >
                  <ReviewStep
                    :basic-data="formData.basicData"
                    :juridical-documentation="formData.juridicalDocumentation"
                    :economic-operational-data="formData.economicOperationalData"
                    :linked-institutions="formData.linkedInstitutions"
                    :additional-attachments="formData.additionalAttachments"
                    :accept-terms="formData.acceptTerms"
                    :errors="getReviewErrors()"
                    :activity-areas="activityAreas"
                    :organization-sizes="organizationSizes"
                    :primary-variant="primaryVariant"
                    :disabled="isFormDisabled"
                    @update:accept-terms="updateAcceptTerms"
                    @show-terms="showTerms = true"
                  />
                </tab-content>

              </form-wizard>
            </div>
          </div>

          <b-row class="m-0">
            <b-col lg="12">
            </b-col>
          </b-row>

          <template #footer>
            <page-footer :showLoginLink="false"></page-footer>
          </template>
        </b-card>

        <!-- Modal de Termos -->
        <b-modal
          v-model="showTerms"
          title="Termos e Condições de Uso"
          size="xl"
          hide-footer
          scrollable
        >
          <div class="terms-content">
            <div class="text-center mb-4">
              <h4 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'">Termos e Condições de Uso</h4>
              <h5 class="text-muted">Sistema de Credenciamento CEBAS</h5>
              <hr>
            </div>

            <div class="text-justify">
              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">1. ACEITAÇÃO DOS TERMOS</h6>
              <p>
                Ao utilizar este sistema de credenciamento CEBAS, você concorda integralmente com os presentes
                termos e condições de uso. Caso não concorde com qualquer disposição aqui prevista,
                não prossiga com a utilização do sistema.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">2. OBJETO E FINALIDADE</h6>
              <p>
                Este sistema destina-se exclusivamente ao processo de solicitação de Certificado de Entidade
                Beneficente de Assistência Social (CEBAS), conforme regulamentação vigente. O sistema permite
                o cadastro, envio de documentação e acompanhamento do processo de credenciamento.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">3. RESPONSABILIDADES DO USUÁRIO</h6>
              <p>O usuário compromete-se a:</p>
              <ul class="pl-4">
                <li>Fornecer informações verdadeiras, completas e atualizadas;</li>
                <li>Manter a confidencialidade de suas credenciais de acesso;</li>
                <li>Utilizar o sistema apenas para os fins previstos;</li>
                <li>Responsabilizar-se pela veracidade de todos os documentos enviados;</li>
                <li>Cumprir a legislação aplicável ao processo de credenciamento;</li>
                <li>Não tentar violar a segurança do sistema ou acessar áreas restritas.</li>
              </ul>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">4. PROTEÇÃO DE DADOS PESSOAIS</h6>
              <p>
                Em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018),
                informamos que:
              </p>
              <ul class="pl-4">
                <li>Os dados pessoais coletados serão utilizados exclusivamente para o processo de credenciamento CEBAS;</li>
                <li>As informações serão tratadas com confidencialidade e segurança adequadas;</li>
                <li>Os dados poderão ser compartilhados com órgãos competentes quando necessário ao processo;</li>
                <li>Você tem direito de acesso, correção, exclusão e portabilidade de seus dados;</li>
                <li>O titular pode revogar o consentimento a qualquer tempo, observadas as obrigações legais.</li>
              </ul>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">5. DOCUMENTAÇÃO E ARQUIVOS</h6>
              <p>
                Quanto à documentação enviada através do sistema:
              </p>
              <ul class="pl-4">
                <li>Todos os documentos devem ser legíveis e em formato PDF;</li>
                <li>O usuário é responsável pela autenticidade dos documentos enviados;</li>
                <li>Os arquivos são armazenados com segurança e podem ser acessados pelos órgãos competentes;</li>
                <li>Documentos com vírus ou malware serão automaticamente rejeitados;</li>
                <li>O sistema não se responsabiliza por perda de dados devido a problemas técnicos do usuário.</li>
              </ul>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">6. LIMITAÇÕES DE RESPONSABILIDADE</h6>
              <p>
                O sistema e seus responsáveis não se responsabilizam por:
              </p>
              <ul class="pl-4">
                <li>Indisponibilidade temporária do sistema por manutenção ou problemas técnicos;</li>
                <li>Decisões administrativas relacionadas ao processo de credenciamento;</li>
                <li>Danos decorrentes do uso inadequado do sistema;</li>
                <li>Perda de dados causada por falhas no equipamento ou conexão do usuário;</li>
                <li>Atrasos no processo causados por documentação inadequada ou incompleta.</li>
              </ul>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">7. PROPRIEDADE INTELECTUAL</h6>
              <p>
                Todo o conteúdo do sistema, incluindo textos, imagens, logos, código-fonte e funcionalidades,
                é protegido por direitos autorais e outras leis de propriedade intelectual. É vedada a
                reprodução, distribuição ou modificação sem autorização expressa.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">8. MODIFICAÇÕES DOS TERMOS</h6>
              <p>
                Estes termos podem ser modificados a qualquer tempo, sendo as alterações comunicadas
                através do próprio sistema. O uso continuado após as modificações implica na aceitação
                dos novos termos.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">9. PRAZO E RESCISÃO</h6>
              <p>
                O acesso ao sistema é concedido pelo prazo necessário à conclusão do processo de
                credenciamento. O acesso pode ser suspenso ou encerrado em caso de descumprimento
                destes termos ou conclusão do processo.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">10. LEGISLAÇÃO APLICÁVEL</h6>
              <p>
                Estes termos são regidos pela legislação brasileira, especialmente:
              </p>
              <ul class="pl-4">
                <li>Lei nº 12.101/2009 (Lei do CEBAS);</li>
                <li>Lei nº 13.709/2018 (Lei Geral de Proteção de Dados);</li>
                <li>Lei nº 12.965/2014 (Marco Civil da Internet);</li>
                <li>Código de Defesa do Consumidor;</li>
                <li>Demais normas aplicáveis ao setor.</li>
              </ul>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">11. CONTATO E SUPORTE</h6>
              <p>
                Para dúvidas, sugestões ou problemas técnicos relacionados ao sistema,
                entre em contato através dos canais oficiais disponibilizados na plataforma.
              </p>

              <h6 :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" class="mt-4 mb-2">12. DECLARAÇÃO DE CIÊNCIA</h6>
              <p>
                Ao aceitar estes termos, você declara que:
              </p>
              <ul class="pl-4">
                <li>Leu e compreendeu integralmente este documento;</li>
                <li>Possui capacidade jurídica para assumir as obrigações aqui previstas;</li>
                <li>Está autorizado a representar a entidade no processo de credenciamento;</li>
                <li>Compromete-se a cumprir todas as disposições aqui estabelecidas.</li>
              </ul>

              <div class="alert alert-info mt-4">
                <div class="alert-body">
                  <h6 class="alert-heading">
                    <i class="feather icon-info mr-1"></i>
                    Importante
                  </h6>
                  <hr>
                  <p class="mb-0">
                    <strong>Data de última atualização:</strong> {{ new Date().toLocaleDateString('pt-BR') }}<br>
                    <strong>Versão:</strong> 1.0<br><br>
                    O processo de credenciamento CEBAS segue rigorosamente a legislação brasileira vigente.
                    Certifique-se de que sua entidade atende a todos os requisitos legais antes de iniciar o processo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </b-modal>

        <!-- Modal para Continuar Credenciamento -->
        <b-modal
          v-model="showContinueModal"
          title="Continuar Credenciamento"
          size="md"
          @cancel="resetContinueModal"
          @hide="resetContinueModal"
          hide-footer
        >
          <div class="text-center mb-3">
            <i
              class="feather icon-search"
              :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"
              style="font-size: 2.5rem;"
            ></i>
            <h5 class="mt-3">Recuperar Credenciamento</h5>
            <p class="text-muted">
              Digite o e-mail e o código de credenciamento recebido por e-mail
            </p>
          </div>

          <b-form @submit.prevent="searchExistingCredentialing">
            <b-row>
              <b-col md="12">
                <b-form-group
                  label="E-mail"
                  label-for="continue-email"
                  description="Digite o mesmo e-mail informado no credenciamento anterior"
                >
                  <b-form-input
                    id="continue-email"
                    v-model="continueEmail"
                    type="email"
                    placeholder="seu@email.com"
                    :state="continueEmailValidation"
                    size="lg"
                    required
                    @input="validateContinueEmail"
                    @keyup.enter="searchExistingCredentialing"
                  />
                  <b-form-invalid-feedback>
                    E-mail válido é obrigatório
                  </b-form-invalid-feedback>
                  <b-form-valid-feedback>
                    E-mail válido
                  </b-form-valid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="12">
                <b-form-group
                  label="Código de Credenciamento"
                  label-for="continue-code"
                  description="Digite o código de 6 dígitos recebido por e-mail"
                >
                  <b-form-input
                    id="continue-code"
                    v-model="continueCode"
                    v-mask="'######'"
                    placeholder="000000"
                    :state="continueCodeValidation"
                    size="lg"
                    maxlength="6"
                    required
                    @input="validateContinueCode"
                    @keyup.enter="searchExistingCredentialing"
                  />
                  <b-form-invalid-feedback>
                    Código de 6 dígitos é obrigatório
                  </b-form-invalid-feedback>
                  <b-form-valid-feedback>
                    Código válido
                  </b-form-valid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Botão de busca separado -->
            <div class="text-center mb-3">
              <b-button
                :variant="primaryVariant"
                :class="!primaryVariant ? 'btn-dynamic-primary' : ''"
                :style="modalButtonStyles"
                size="lg"
                @click="searchExistingCredentialing"
                :disabled="!canSearchCredentialing || searchingCredentialing"
                class="px-4"
              >
                <b-spinner v-if="searchingCredentialing" small class="mr-2"></b-spinner>
                <i v-else class="feather icon-search mr-2"></i>
                {{ searchingCredentialing ? 'Buscando...' : 'Buscar Credenciamento' }}
              </b-button>
            </div>

            <b-alert variant="info" show class="mt-3">
              <div class="alert-body">
                <small>
                  <strong>Dica:</strong> O código de credenciamento é enviado por e-mail quando você inicia o processo.
                  Se você não possui o código, inicie um novo credenciamento clicando em "Iniciar Credenciamento".
                </small>
              </div>
            </b-alert>
          </b-form>

          <!-- Botão fechar ajustado -->
          <div class="text-center mt-4">
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="closeModal"
              class="px-3"
            >
              <i class="feather icon-x mr-1"></i>
              Fechar
            </b-button>
          </div>
        </b-modal>

        <!-- Modal para Iniciar Credenciamento -->
        <b-modal
          v-model="showStartModal"
          title="Iniciar Credenciamento"
          size="md"
          @cancel="resetStartModal"
          @hide="resetStartModal"
          hide-footer
        >
          <div class="text-center mb-3">
            <i
              class="feather icon-mail"
              :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"
              style="font-size: 2.5rem;"
            ></i>
            <h5 class="mt-3">Verificação de E-mail</h5>
            <p class="text-muted">
              Informe seu e-mail para receber o código de validação
            </p>
          </div>

          <!-- Alert de sucesso - ANTES do formulário -->
          <b-alert v-if="codeSent && generatedCode" variant="success" show class="mb-4">
            <div class="alert-body">
              <h6 class="alert-heading">
                <i class="feather icon-check-circle mr-1"></i>
                Código enviado!
              </h6>
              <hr>
              <p class="mb-2">
                Um código de validação foi enviado para <strong>{{ startEmail }}</strong>
              </p>
              <p class="mb-0">
                <small><strong>Código de validação gerado:</strong> {{ generatedCode }}</small>
              </p>
            </div>
          </b-alert>

          <b-form @submit.prevent="sendVerificationCode">
            <b-form-group
              label="E-mail"
              label-for="start-email"
              description="Este e-mail será usado durante todo o processo de credenciamento e não poderá ser alterado posteriormente"
            >
              <b-form-input
                id="start-email"
                v-model="startEmail"
                type="email"
                placeholder="seu@email.com"
                :state="startEmailValidation"
                size="lg"
                required
                @input="validateStartEmail"
                @keyup.enter="sendVerificationCode"
              />
              <b-form-invalid-feedback>
                E-mail válido é obrigatório
              </b-form-invalid-feedback>
              <b-form-valid-feedback>
                E-mail válido
              </b-form-valid-feedback>
            </b-form-group>

            <!-- Botão de envio -->
            <div class="text-center mb-3">
              <b-button
                :variant="primaryVariant"
                :class="!primaryVariant ? 'btn-dynamic-primary' : ''"
                :style="modalButtonStyles"
                size="lg"
                @click="sendVerificationCode"
                :disabled="!isStartEmailValid || sendingCode"
                class="px-4"
              >
                <b-spinner v-if="sendingCode" small class="mr-2"></b-spinner>
                <i v-else class="feather icon-send mr-2"></i>
                {{ sendingCode ? 'Iniciando...' : 'Iniciar preenchimento' }}
              </b-button>
            </div>

            <!-- Botão para prosseguir (só aparece após envio) -->
            <div v-if="codeSent" class="text-center mb-3">
              <b-button
                :variant="primaryVariant"
                :class="!primaryVariant ? 'btn-dynamic-primary' : ''"
                :style="modalButtonStyles"
                size="lg"
                @click="startCredentialingProcess"
                class="px-4"
              >
                <i class="feather icon-arrow-right mr-2"></i>
                Prosseguir com Credenciamento
              </b-button>
            </div>

            <b-alert variant="info" show class="mt-3">
              <div class="alert-body">
                <small>
                  <strong>Importante:</strong> Vai ser gerado um código de validação o qual será necessário para recuperar
                  seus dados caso precise continuar o credenciamento posteriormente.
                  <span v-if="!codeSent"> Após inserir seu e-mail, clique em "Iniciar Credenciamento" para receber o código por e-mail.</span>
                  <span v-else> Se alterar o e-mail, um novo código será gerado.</span>
                </small>
              </div>
            </b-alert>
          </b-form>

          <!-- Botão fechar ajustado -->
          <div class="text-center mt-4">
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="closeStartModal"
              class="px-3"
            >
              <i class="feather icon-x mr-1"></i>
              Fechar
            </b-button>
          </div>
        </b-modal>

        <!-- Modal de Sucesso -->
        <b-modal
          v-model="showSuccessModal"
          title="Solicitação Enviada!"
          size="md"
          hide-footer
          @hide="resetForm"
          @cancel="resetForm"
        >
          <div class="text-center">
            <i class="feather icon-check-circle text-success" style="font-size: 3rem;"></i>
            <h5 class="mt-3">Sucesso!</h5>
            <p>Sua solicitação foi enviada com sucesso. Você receberá um email de confirmação em breve.</p>
            <p class="text-primary">Protocolo gerado: {{ protocolNumber }}</p>
            <small>Obrigado por utilizar o sistema de credenciamento CEBAS da SECURAMDATA.</small>

            <!-- Botão customizado com cores dinâmicas -->
            <div class="mt-4">
              <b-button
                :class="primaryVariant ? `btn-${primaryVariant}` : 'btn-dynamic-primary'"
                size="md"
                @click="closeSuccessModal"
              >
                <i class="feather icon-check mr-2"></i>
                Fechar
              </b-button>
            </div>
          </div>
        </b-modal>

        <!-- Painel de Acessibilidade -->
        <AccessibilityPanel :primary-color="primaryColor" />
      </main>
    </div>
  </div>
</template>

<script>

import {
  BForm,
  BFormGroup,
  BRow,
  BCol,
  BLink,
  BCard,
  BCardText,
  BCardTitle,
  BCardFooter,
  BCardImg,
  BImg,
  BInputGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormSelectOption,
  BFormFile,
  BFormDatepicker,
  BFormCheckbox,
  BFormInvalidFeedback,
  BFormValidFeedback,
  BButton,
  BAlert,
  VBTooltip,
  BModal,
  BContainer,
  BBadge,
  BSpinner
} from 'bootstrap-vue'

import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { mask } from 'vue-the-mask'

import CebasCredentialingService from '@/services/api/cebas/CebasCredentialingService'
import PageFooter from '@/components/PageFooter.vue'
import ComplexEditorView from "@/components/ComplexEditorView.vue"
import AccessibilityPanel from "@/components/AccessibilityPanel.vue"
import BasicDataStep from "./steps/BasicDataStep.vue"
import JuridicalDocumentationStep from "./steps/JuridicalDocumentationStep.vue"
import EconomicOperationalDataStep from "./steps/EconomicOperationalDataStep.vue"
import LinkedInstitutionsStep from "./steps/LinkedInstitutionsStep.vue"
import AdditionalAttachmentsStep from "./steps/AdditionalAttachmentsStep.vue"
import ReviewStep from "./steps/ReviewStep.vue"

export default {
  name: 'CredentialingPublic',
  directives: {
    'b-tooltip': VBTooltip,
    mask
  },
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    BFormFile,
    BFormDatepicker,
    BFormCheckbox,
    BFormInvalidFeedback,
    BFormValidFeedback,
    BLink,
    BCard,
    BCardText,
    BCardTitle,
    BCardFooter,
    BCardImg,
    BImg,
    BButton,
    BAlert,
    VBTooltip,
    BModal,
    BContainer,
    PageFooter,
    ComplexEditorView,
    AccessibilityPanel,
    FormWizard,
    TabContent,
    BBadge,
    BSpinner,
    BasicDataStep,
    JuridicalDocumentationStep,
    EconomicOperationalDataStep,
    LinkedInstitutionsStep,
    AdditionalAttachmentsStep,
    ReviewStep
  },
  props: {
    link: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      portalCebasConfig: {
        id: null,
        title_page: '',
        link: '',
        info_page: '',
        color: '',
      },
      link: null,
      credentialingId: null,
      formData: {
        basicData: {},
        juridicalDocumentation: {},
        economicOperationalData: {
          certificate_period_start: null,
          certificate_period_end: null,
          scholarship_count: '',
          scholarship_types: [],
          support_services: '',
          report_activities: null,
          summary_report_scholarships: null,
          list_scholarships: null,
          analysis_socioeconomic_students: null,
          accounting_regime: null,
          accounting_reports: null,
          accounting_gratuity_demo: null,
          audit_opinion: null,
          revenue_received: null,
          service_plan: null,
          social_assistance_registration: null
        },
        linkedInstitutions: [],
        additionalAttachments: {
          additional_attachments: []
        }
      },
      errors: {},
      service: null,
      showTerms: false,
      showSuccessModal: false,
      validationErrors: {},
      formStarted: false,
      initialAcceptance: {
        dataVeracity: false,
        dataCollection: false
      },
      showContinueModal: false,
      continueEmail: '',
      continueEmailValidation: null,
      continueCode: '',
      continueCodeValidation: null,
      searchingCredentialing: false,
      showStartModal: false,
      startEmail: '',
      startEmailValidation: null,
      sendingCode: false,
      codeSent: false,
      generatedCode: null,
      isSaving: false,
      lastSaveTime: null,
      autoSaveEnabled: true,
      saveErrors: {},
      currentStep: null,
      isFormCompleted: false,
      protocolNumber: null,
      // Propriedades para badge flutuante
      floatingSavingBadge: false,
      originalBadgePosition: null
    }
  },
  computed: {
    hasCebasCredentialing() {
      return (this.portalCebasConfig && this.portalCebasConfig.id != null)
    },
    canStartProcess() {
      return this.initialAcceptance.dataVeracity && this.initialAcceptance.dataCollection
    },

    // Controle de desabilitação baseado no status
    isFormDisabled() {
      // Status que desabilitam o formulário: SU (Submitted), UR (Under review), AP (Approved), RE (Rejected)
      const disabledStatuses = ['SU', 'UR', 'AP', 'RE'];
      return this.formData.status && this.formData.status.key && disabledStatuses.includes(this.formData.status.key);
    },

    canEditForm() {
      return !this.isFormDisabled;
    },

    // Textos dinâmicos dos botões baseado no status
    nextButtonText() {
      return this.isFormDisabled ? 'Próximo →' : 'Próximo';
    },

    backButtonText() {
      return this.isFormDisabled ? '← Anterior' : 'Anterior';
    },

    finishButtonText() {
      if (this.isFormDisabled) {
        return 'Formulário Finalizado';
      }
      return 'Enviar Solicitação';
    },

    // Detectar ambiente de desenvolvimento
    isDevelopment() {
      return process.env.NODE_ENV === 'development' ||
             window.location.hostname === 'localhost' ||
             window.location.hostname === '127.0.0.1' ||
             window.location.hostname.includes('localhost')
    },

    // Conteúdo da página de informações com fallback padrão
    infoPageContent() {
      // 1. Prioridade: Valor vindo da API/objeto portalCebasConfig
      if (this.portalCebasConfig && this.portalCebasConfig.info_page && this.portalCebasConfig.info_page.trim()) {
        return this.portalCebasConfig.info_page;
      }

      // 2. Fallback: Conteúdo padrão configurado
      return `
        <div class="info-page-default">
          <h4 class="mb-3">Bem-vindo ao Processo de Credenciamento CEBAS</h4>

          <p class="mb-3">
            O Certificado de Entidade Beneficente de Assistência Social (CEBAS) é um certificado concedido pelo governo federal às entidades da sociedade civil que prestam serviços nas áreas de educação, saúde ou assistência social.
          </p>

          <div class="alert alert-info">
            <h6 class="alert-heading">
              <i class="feather icon-info mr-1"></i>
              Sobre este Processo
            </h6>
            <hr>
            <p class="mb-2"><strong>Objetivo:</strong> Solicitar o credenciamento ou recredenciamento CEBAS para sua entidade</p>
            <p class="mb-2"><strong>Validade:</strong> O certificado tem validade de 3 anos, podendo ser renovado</p>
            <p class="mb-0"><strong>Requisitos:</strong> Atender aos critérios estabelecidos pela legislação vigente</p>
          </div>

          <h6 class="mt-4 mb-2">Documentação Necessária:</h6>
          <ul class="mb-3">
            <li>Estatuto social ou ato constitutivo registrado</li>
            <li>Ata de eleição da diretoria atual</li>
            <li>Comprovante de inscrição no CNPJ</li>
            <li>Certificado digital A3 ou e-CPF do representante legal</li>
            <li>Documentos específicos conforme área de atuação</li>
          </ul>

          <p class="mb-3">
            <strong>Referência oficial:</strong>
            <a href="https://www.gov.br/mec/pt-br/cebas/requerimento-passo-a-passo"
               target="_blank"
               rel="noopener noreferrer"
               class="text-primary">
              Passo a passo oficial do MEC
              <i class="feather icon-external-link ml-1"></i>
            </a>
          </p>

          <div class="alert alert-warning">
            <p class="mb-0">
              <i class="feather icon-alert-triangle mr-1"></i>
              <strong>Importante:</strong> Certifique-se de ter todos os documentos em formato digital (PDF) antes de iniciar o processo.
            </p>
          </div>
        </div>
      `;
    },
    primaryColor() {
      // 1. Prioridade: Cor customizada vinda da API
      if (this.portalCebasConfig && this.portalCebasConfig.color) {
        console.log('Processing color from API:', this.portalCebasConfig.color);
        // Verifica se é um nome de cor do sistema (primary, success, etc.)
        if (this.isSystemColorName(this.portalCebasConfig.color)) {
          const systemColor = this.getSystemColor(this.portalCebasConfig.color);
          console.log('System color resolved to:', systemColor);
          return systemColor;
        }
        // Se for uma cor em hex/rgb, usa diretamente
        console.log('Using direct color value:', this.portalCebasConfig.color);
        return this.portalCebasConfig.color;
      }

      // 2. Fallback: Tenta obter a cor primária das variáveis CSS do projeto
      const root = getComputedStyle(document.documentElement);

      const possibleVars = [
        '--bs-primary',
        '--primary',
        '--vs-primary',
        '--theme-primary',
        '--color-primary'
      ];

      for (const varName of possibleVars) {
        const color = root.getPropertyValue(varName).trim();
        if (color && color !== '') {
          console.log('Using CSS variable color:', color);
          return color;
        }
      }

      // 3. Fallback final: Cor primária padrão do projeto
      const fallbackColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim() || '#156440';
      console.log('Using fallback color:', fallbackColor);
      return fallbackColor;
    },

    // Variante para componentes Bootstrap Vue
    primaryVariant() {
      // Se é uma cor customizada (hex, rgb, etc), retorna null para usar CSS customizado
      if (this.portalCebasConfig && this.portalCebasConfig.color && !this.isSystemColorName(this.portalCebasConfig.color)) {
        return null;
      }

      // Se é nome de cor do sistema, retorna o nome
      if (this.portalCebasConfig && this.portalCebasConfig.color && this.isSystemColorName(this.portalCebasConfig.color)) {
        return this.portalCebasConfig.color;
      }

      // IMPORTANTE: Quando não há configuração da API, retorna null para usar CSS dinâmico
      return null;
    },

    // Estilos CSS dinâmicos para elementos customizados
    dynamicStyles() {
      const color = this.primaryColor;
      console.log('Setting dynamic styles with color:', color);
      const styles = {
        '--dynamic-primary': color,
        '--dynamic-primary-rgb': this.hexToRgb(color),
        '--dynamic-primary-light': this.lightenColor(color, 0.1),
        '--dynamic-primary-dark': this.darkenColor(color, 0.1)
      };
      console.log('Generated dynamic styles:', styles);

      // Aplicar as variáveis globalmente para que modais também tenham acesso
      this.setGlobalCSSVariables(styles);

      return styles;
    },

    // Validação do e-mail para continuar credenciamento
    isContinueEmailValid() {
      return this.continueEmail && this.isValidEmail(this.continueEmail);
    },

    // Validação do código para continuar credenciamento
    isContinueCodeValid() {
      return this.continueCode && this.continueCode.length === 6;
    },

    // Validação geral para habilitar busca
    canSearchCredentialing() {
      return this.isContinueEmailValid && this.isContinueCodeValid;
    },

    // Validação do e-mail para iniciar credenciamento
    isStartEmailValid() {
      return this.startEmail && this.isValidEmail(this.startEmail);
    },

    // Estilos específicos para botões do modal
    modalButtonStyles() {
      if (this.primaryVariant) {
        return {}; // Usa variant do Bootstrap
      }

      // Para cores customizadas, usa estilos inline
      return {
        'background-color': `${this.primaryColor} !important`,
        'border-color': `${this.primaryColor} !important`,
        'color': '#fff !important'
      };
    },
  },
  watch: {
    // Watcher para resetar dados quando e-mail de início for alterado
    startEmail(newEmail, oldEmail) {
      // Se o e-mail mudou e havia dados anteriores, resetar tudo
      if (oldEmail && newEmail !== oldEmail && (this.codeSent || this.generatedCode)) {
        this.resetVerificationData();
        this.$vs.notify({
          title: 'E-mail alterado',
          text: 'O código anterior foi invalidado. Digite o novo e-mail e envie um novo código.',
          color: 'warning'
        });
      }
    },

    // Watcher para verificar posição do badge quando isSaving muda
    isSaving(newValue) {
      this.$nextTick(() => {
        this.checkFloatingBadgePosition();
      });
    }
  },
  methods: {
    // ========================================
    // MÉTODOS DE SALVAMENTO AUTOMÁTICO - API
    // ========================================

    /**
     * Salva os dados do formulário na API
     * @param {string} currentStep - Step atual que está sendo salvo
     * @param {boolean} showLoading - Se deve exibir loading
     * @returns {Promise<boolean>} - Sucesso do salvamento
     */
    async saveFormData(currentStep = null, showLoading = true) {
      try {
        if (showLoading) {
          this.isSaving = true;
          // Verifica posição do badge quando inicia o salvamento
          this.$nextTick(() => {
            this.checkFloatingBadgePosition();
          });
        }

        // Verifica se precisa enviar arquivos (para dados econômicos operacionais)
        const needsFileUpload = currentStep === 'economic_operational_data' &&
                               (this.isFileObject(this.formData.economicOperationalData.report_activities) ||
                                this.isFileObject(this.formData.economicOperationalData.summary_report_scholarships));

        let response;

        if (needsFileUpload) {
          // Cria FormData para envio de arquivos
          const formData = new FormData();

          // Adiciona dados básicos
          formData.append('credentialing_id', this.credentialingId);
          formData.append('step', currentStep);
          formData.append('steps_data', JSON.stringify({
            basic_data: this.formData.basicData,
            juridical_documentation: this.formData.juridicalDocumentation,
            economic_operational_data: {
              certificate_period_start: this.formData.economicOperationalData.certificate_period_start,
              certificate_period_end: this.formData.economicOperationalData.certificate_period_end,
              scholarship_count: this.formData.economicOperationalData.scholarship_count,
              scholarship_types: this.formData.economicOperationalData.scholarship_types,
              support_services: this.formData.economicOperationalData.support_services
            },
            linked_institutions: this.formData.linkedInstitutions,
            additional_attachments: this.formData.additionalAttachments
          }));
          formData.append('form_state', JSON.stringify({
            current_step: currentStep,
            is_completed: this.isFormCompleted,
            last_updated: new Date().toISOString()
          }));
          formData.append('status', this.formData.status);

          // Adiciona arquivos se existirem
          if (this.isFileObject(this.formData.economicOperationalData.report_activities)) {
            formData.append('report_activities', this.formData.economicOperationalData.report_activities);
          }
          if (this.isFileObject(this.formData.economicOperationalData.summary_report_scholarships)) {
            formData.append('summary_report_scholarships', this.formData.economicOperationalData.summary_report_scholarships);
          }

          response = await this.service.saveFormDataWithFiles(formData);
        } else {
          // Envio normal sem arquivos
          const data = {
            credentialing_id: this.credentialingId,
            step: currentStep,
            steps_data: {
              basic_data: this.formData.basicData,
              juridical_documentation: this.formData.juridicalDocumentation,
              economic_operational_data: this.formData.economicOperationalData,
              linked_institutions: this.formData.linkedInstitutions,
              additional_attachments: this.formData.additionalAttachments
            },
            form_state: {
              current_step: currentStep,
              is_completed: this.isFormCompleted,
              last_updated: new Date().toISOString()
            },
            status: this.formData.status
          };

          response = await this.service.saveFormData(data);
        }

        // Só mostra notificação de sucesso se não for submissão final
        if (currentStep !== 'final_submission') {
          this.$vs.notify({
            title: 'Dados salvos com sucesso',
            text: 'Os dados foram salvos com sucesso.',
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }

        // Scroll para o topo após salvamento bem-sucedido
        this.scrollToTop();

        return response;
      } catch (error) {
        console.error('Erro ao salvar dados:', error);

        // Só mostra notificação de erro se não for submissão final
        if (currentStep !== 'final_submission') {
          this.$vs.notify({
            title: 'Erro',
            text: 'Ocorreu um erro ao salvar os dados. Por favor, tente novamente.',
            color: 'danger',
            position: 'bottom-right'
          });
        }

        // Relança a exceção para que o método chamador possa tratá-la
        throw error;
      } finally {
        if (showLoading) {
          this.isSaving = false;
          this.lastSaveTime = new Date();
          // Verifica posição do badge quando termina o salvamento
          this.$nextTick(() => {
            this.checkFloatingBadgePosition();
          });
        }
      }
    },

    /**
     * Prepara os dados do formulário no formato adequado para API
     * @param {string} currentStep - Step atual
     * @returns {Object} - Dados formatados para API
     */
    prepareDataForAPI(currentStep) {
      const baseData = {
        // Dados de identificação
        id: this.formData.id || this.credentialingId || null,
        process_code: this.formData.process_code || null,
        email: this.formData.basicData.email || this.startEmail,
        current_step: currentStep,
        last_update: new Date().toISOString(),

        // Dados organizados por step (como solicitado)
        steps_data: {
          basic_data: this.formData.basicData,
          juridical_documentation: this.formData.juridicalDocumentation,
          economic_operational_data: this.formData.economicOperationalData,
          linked_institutions: this.formData.linkedInstitutions,
          additional_attachments: this.formData.additionalAttachments
        },

        // Estado do formulário
        form_state: {
          accept_terms: this.formData.acceptTerms,
          initial_acceptance: this.initialAcceptance,
          form_started: this.formStarted,
          current_step_index: this.getCurrentStepIndex()
        }
      };

      return baseData;
    },

    /**
     * Método placeholder para ser implementado no service
     * @param {Object} data - Dados a serem salvos
     * @returns {Promise} - Promise da requisição
     */
    async saveCredentialingData(data) {
      // TODO: Implementar no service
      // Exemplo de como será a implementação:
      return this.service.saveFormData(data);

      // Simulação para desenvolvimento
      if (this.isDevelopment) {
        console.log('📤 Dados para salvar na API:', data);

        // Simular delay da API
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Simular resposta de sucesso
        return {
          success: true,
          message: 'Dados salvos com sucesso',
          saved_at: new Date().toISOString()
        };
      }

      throw new Error('Service não implementado ainda');
    },

    /**
     * Obtém o índice do step atual do wizard
     * @returns {number} - Índice do step atual
     */
    getCurrentStepIndex() {
      if (this.$refs.credentialingWizard) {
        return this.$refs.credentialingWizard.activeTabIndex || 0;
      }
      return 0;
    },

    /**
     * Obtém o nome do step baseado no índice
     * @param {number} index - Índice do step
     * @returns {string} - Nome do step
     */
    getStepNameByIndex(index) {
      const stepNames = [
        'basic_data',
        'juridical_documentation',
        'economic_operational_data',
        'linked_institutions',
        'additional_attachments',
        'review'
      ];

      return stepNames[index] || 'unknown_step';
    },

    /**
     * Obtém o título em português do step baseado no nome técnico
     * @param {string} stepName - Nome técnico do step
     * @returns {string} - Título em português
     */
    getStepTitle(stepName) {
      const stepTitles = {
        'basic_data': 'Dados Básicos',
        'juridical_documentation': 'Documentação Jurídica',
        'economic_operational_data': 'Dados Econômicos e Operacionais',
        'linked_institutions': 'Unidades Vinculadas',
        'additional_attachments': 'Anexos Complementares',
        'review': 'Revisão',
        'final_submission': 'Envio Final'
      };

      return stepTitles[stepName] || stepName;
    },

    /**
     * Formata o tempo do último salvamento
     * @returns {string} - Tempo formatado
     */
    formatLastSaveTime() {
      if (!this.lastSaveTime) return '';

      const now = new Date();
      const diffInSeconds = Math.floor((now - this.lastSaveTime) / 1000);

      if (diffInSeconds < 60) {
        return `há ${diffInSeconds} segundos`;
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`;
      } else {
        return this.lastSaveTime.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },

    // ========================================
    // VALIDAÇÕES POR ETAPA (COM SALVAMENTO)
    // ========================================

    // Validações por etapa
    async validateBasicData() {
      // Modo visualização: permite navegação sem validar nem salvar
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - navegação permitida sem validação (Basic Data)');
        return true;
      }

      const errors = {}
      const basic = this.formData.basicData

      if (!basic.credentialing_type) errors['basicData.credentialing_type'] = true
      if (!basic.activity_area) errors['basicData.activity_area'] = true
      if (!basic.entity_name?.trim()) errors['basicData.entity_name'] = true
      if (!basic.cnpj?.trim() || !this.isValidCNPJ(basic.cnpj)) errors['basicData.cnpj'] = true
      if (!basic.main_cnae?.trim()) errors['basicData.main_cnae'] = true
      if (!basic.address?.trim()) errors['basicData.address'] = true
      if (!basic.phone?.trim()) errors['basicData.phone'] = true
      if (!basic.email?.trim() || !this.isValidEmail(basic.email)) errors['basicData.email'] = true
      if (!basic.legal_representative_name?.trim()) errors['basicData.legal_representative_name'] = true
      if (!basic.legal_representative_cpf?.trim() || !this.isValidCPF(basic.legal_representative_cpf)) errors['basicData.legal_representative_cpf'] = true
      if (!basic.legal_representative_position?.trim()) errors['basicData.legal_representative_position'] = true
      if (!basic.legal_representative_email?.trim() || !this.isValidEmail(basic.legal_representative_email)) errors['basicData.legal_representative_email'] = true

      this.validationErrors = { ...this.validationErrors, ...errors }

      if (Object.keys(errors).length > 0) {
        this.notifyError(this.$vs, 'Por favor, preencha todos os campos obrigatórios.')
        return false
      }

      // Salvar dados na API após validação bem-sucedida
      const saveSuccess = await this.saveFormData('basic_data', true);
      if (!saveSuccess) {
        // Se não conseguiu salvar, ainda permite prosseguir mas avisa o usuário
        console.warn('Dados não foram salvos na API, mas validação passou');
      }

      return true
    },

    async validateJuridicalDocumentation() {
      // Modo visualização: permite navegação sem validar nem salvar
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - navegação permitida sem validação (Juridical Documentation)');
        return true;
      }

      const errors = {}
      const juridical = this.formData.juridicalDocumentation

      if (!juridical.statute) errors['juridicalDocumentation.statute'] = true
      if (!juridical.election_minutes) errors['juridicalDocumentation.election_minutes'] = true
      if (!juridical.cnpj_certificate) errors['juridicalDocumentation.cnpj_certificate'] = true
      if (!juridical.identification_members) errors['juridicalDocumentation.identification_members'] = true
      // foundation_document não é obrigatório

      this.validationErrors = { ...this.validationErrors, ...errors }

      if (Object.keys(errors).length > 0) {
        this.notifyError(this.$vs, 'Por favor, preencha todos os campos obrigatórios da documentação jurídica.')
        return false
      }

      // Salvar dados na API após validação bem-sucedida
      const saveSuccess = await this.saveFormData('juridical_documentation', true);
      if (!saveSuccess) {
        console.warn('Dados não foram salvos na API, mas validação passou');
      }

      return true
    },

    async validateEconomicOperationalData() {
      // Modo visualização: permite navegação sem validar nem salvar
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - navegação permitida sem validação (Economic Operational Data)');
        return true;
      }

      const errors = {}
      const economicOperational = this.formData.economicOperationalData

      // Debug: Log dos dados para investigação
      if (this.isDevelopment) {
        console.log('Validando dados econômicos:', economicOperational)
      }

      if (!economicOperational.certificate_period_start) errors['economicOperationalData.certificate_period_start'] = true
      if (!economicOperational.certificate_period_end) errors['economicOperationalData.certificate_period_end'] = true
      if (!economicOperational.scholarship_count || economicOperational.scholarship_count <= 0) errors['economicOperationalData.scholarship_count'] = true
      if (!economicOperational.scholarship_types || !economicOperational.scholarship_types.length) errors['economicOperationalData.scholarship_types'] = true
      if (!economicOperational.report_activities || !economicOperational.report_activities.uploaded) errors['economicOperationalData.report_activities'] = true
      if (!economicOperational.summary_report_scholarships || !economicOperational.summary_report_scholarships.uploaded) errors['economicOperationalData.summary_report_scholarships'] = true
      if (!economicOperational.list_scholarships || !economicOperational.list_scholarships.uploaded) errors['economicOperationalData.list_scholarships'] = true
      if (!economicOperational.analysis_socioeconomic_students || !economicOperational.analysis_socioeconomic_students.uploaded) errors['economicOperationalData.analysis_socioeconomic_students'] = true
      if (!economicOperational.accounting_regime || !economicOperational.accounting_regime.uploaded) errors['economicOperationalData.accounting_regime'] = true
      if (!economicOperational.accounting_reports || !economicOperational.accounting_reports.uploaded) errors['economicOperationalData.accounting_reports'] = true
      if (!economicOperational.accounting_gratuity_demo || !economicOperational.accounting_gratuity_demo.uploaded) errors['economicOperationalData.accounting_gratuity_demo'] = true
      if (!economicOperational.audit_opinion || !economicOperational.audit_opinion.uploaded) errors['economicOperationalData.audit_opinion'] = true
      if (!economicOperational.revenue_received || !economicOperational.revenue_received.uploaded) errors['economicOperationalData.revenue_received'] = true
      if (!economicOperational.service_plan || !economicOperational.service_plan.uploaded) errors['economicOperationalData.service_plan'] = true
      // support_services não é obrigatório

      this.validationErrors = { ...this.validationErrors, ...errors }

      if (Object.keys(errors).length > 0) {
        if (this.isDevelopment) {
          console.log('Erros de validação econômica:', errors)
        }
        this.notifyError(this.$vs, 'Por favor, preencha todos os campos obrigatórios dos dados econômicos e operacionais.')
        return false
      }

      // Salvar dados na API após validação bem-sucedida
      const saveSuccess = await this.saveFormData('economic_operational_data', true);
      if (!saveSuccess) {
        console.warn('Dados não foram salvos na API, mas validação passou');
      }

      return true
    },

    async validateLinkedInstitutions() {
      // Modo visualização: permite navegação sem validar nem salvar
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - navegação permitida sem validação (Linked Institutions)');
        return true;
      }

      if (this.$refs.linkedInstitutionsStep && this.$refs.linkedInstitutionsStep.validateStep) {
        const canProceed = await this.$refs.linkedInstitutionsStep.validateStep();
        if (!canProceed) return false;
      }
      const saveSuccess = await this.saveFormData('linked_institutions', true);
      if (!saveSuccess) {
        console.warn('Dados não foram salvos na API, mas validação passou');
      }
      return true;
    },

    async validateAdditionalAttachments() {
      // Modo visualização: permite navegação sem validar nem salvar
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - navegação permitida sem validação (Additional Attachments)');
        return true;
      }

      // Anexos complementares são opcionais, sempre válido

      // Salvar dados na API mesmo que seja opcional
      const saveSuccess = await this.saveFormData('additional_attachments', true);
      if (!saveSuccess) {
        console.warn('Dados não foram salvos na API, mas validação passou');
      }

      return true
    },

    // Helpers de validação
    isValidCPF(cpf) {
      return cpf && cpf.replace(/\D/g, '').length === 11
    },

    isValidCNPJ(cnpj) {
      return cnpj && cnpj.replace(/\D/g, '').length === 14
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    notifyError(vs, message) {
      // Método para notificar erros usando vuesax
      if (vs && vs.notify) {
        vs.notify({
          title: 'Erro de validação',
          text: message,
          color: 'danger',
          position: 'top-right'
        });
      } else {
        // Fallback para alert se vuesax não estiver disponível
        alert(message);
      }
    },

    getFieldState(fieldPath) {
      if (this.validationErrors[fieldPath]) {
        return false
      }
      return null
    },

    // Formatação de dados
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('pt-BR')
    },

    getActivityAreaLabel(value) {
      const area = this.activityAreas.find(item => item.value === value)
      return area ? area.text : ''
    },

    getOrganizationSizeLabel(value) {
      const size = this.organizationSizes.find(item => item.value === value)
      return size ? size.text : ''
    },

    // Eventos do wizard
    handleStepChange(prevIndex, nextIndex) {
      // Limpar erros do passo anterior
      if (nextIndex > prevIndex) {
        this.clearStepErrors(prevIndex)
      }

      // Scroll automático para o topo ao mudar de step
      this.scrollToTop()
    },

    // Função para scroll automático para o topo
    scrollToTop() {
      this.$nextTick(() => {
        // Scroll suave para o topo da página
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })

        // Alternativamente, scroll para o início do wizard
        const wizardContainer = document.querySelector('.wizard-container')
        if (wizardContainer) {
          wizardContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      })
    },

    clearStepErrors(stepIndex) {
      const stepFields = {
        0: ['basicData.credentialing_type', 'basicData.activity_area', 'basicData.entity_name', 'basicData.cnpj', 'basicData.main_cnae', 'basicData.address', 'basicData.phone', 'basicData.email', 'basicData.legal_representative_name', 'basicData.legal_representative_cpf', 'basicData.legal_representative_position', 'basicData.legal_representative_email'],
        1: ['juridicalDocumentation.statute', 'juridicalDocumentation.election_minutes', 'juridicalDocumentation.foundation_document', 'juridicalDocumentation.cnpj_certificate', 'juridicalDocumentation.identification_members'],
        2: ['economicOperationalData.certificate_period_start', 'economicOperationalData.certificate_period_end', 'economicOperationalData.scholarship_count', 'economicOperationalData.scholarship_types', 'economicOperationalData.report_activities', 'economicOperationalData.summary_report_scholarships', 'economicOperationalData.list_scholarships', 'economicOperationalData.analysis_socioeconomic_students', 'economicOperationalData.accounting_regime', 'economicOperationalData.accounting_reports', 'economicOperationalData.accounting_gratuity_demo', 'economicOperationalData.audit_opinion', 'economicOperationalData.revenue_received', 'economicOperationalData.service_plan', 'economicOperationalData.social_assistance_registration', 'economicOperationalData.support_services'],
        3: ['linkedInstitutions.institution_name', 'linkedInstitutions.institution_cnpj', 'linkedInstitutions.city_id', 'linkedInstitutions.city_name', 'linkedInstitutions.state_id', 'linkedInstitutions.state_name', 'linkedInstitutions.institution_authorization', 'linkedInstitutions.school_regulations'],
        4: ['additionalAttachments.additional_attachments']
      }

      if (stepFields[stepIndex]) {
        stepFields[stepIndex].forEach(field => {
          if (this.validationErrors[field]) {
            delete this.validationErrors[field]
          }
        })
      }
    },

    // Métodos para comunicação com componentes
    getBasicDataErrors() {
      const errors = {}
      Object.keys(this.validationErrors).forEach(key => {
        if (key.startsWith('basicData.')) {
          const fieldName = key.replace('basicData.', '')
          errors[fieldName] = this.validationErrors[key]
        }
      })
      return errors
    },

    getJuridicalDocumentationErrors() {
      const errors = {}
      Object.keys(this.validationErrors).forEach(key => {
        if (key.startsWith('juridicalDocumentation.')) {
          const fieldName = key.replace('juridicalDocumentation.', '')
          errors[fieldName] = this.validationErrors[key]
        }
      })
      return errors
    },

    getEconomicOperationalDataErrors() {
      const errors = {}
      Object.keys(this.validationErrors).forEach(key => {
        if (key.startsWith('economicOperationalData.')) {
          const fieldName = key.replace('economicOperationalData.', '')
          errors[fieldName] = this.validationErrors[key]
        }
      })
      return errors
    },

    getLinkedInstitutionsErrors() {
      const errors = {}
      Object.keys(this.validationErrors).forEach(key => {
        if (key.startsWith('linkedInstitutions.')) {
          const fieldName = key.replace('linkedInstitutions.', '')
          errors[fieldName] = this.validationErrors[key]
        }
      })
      return errors
    },

    getAdditionalAttachmentsErrors() {
      const errors = {}
      Object.keys(this.validationErrors).forEach(key => {
        if (key.startsWith('additionalAttachments.')) {
          const fieldName = key.replace('additionalAttachments.', '')
          errors[fieldName] = this.validationErrors[key]
        }
      })
      return errors
    },

    getReviewErrors() {
      const errors = {}
      if (this.validationErrors.acceptTerms) {
        errors.acceptTerms = this.validationErrors.acceptTerms
      }
      return errors
    },

    // Métodos de atualização dos dados dos componentes
    updateBasicData(newData) {
      this.formData.basicData = { ...this.formData.basicData, ...newData }
      // Limpar erros relacionados aos campos atualizados
      Object.keys(newData).forEach(field => {
        const errorKey = `basicData.${field}`
        if (this.validationErrors[errorKey]) {
          delete this.validationErrors[errorKey]
        }
      })
    },

    updateJuridicalDocumentation(newData) {
      this.formData.juridicalDocumentation = { ...this.formData.juridicalDocumentation, ...newData }
      // Limpar erros relacionados aos campos atualizados
      Object.keys(newData).forEach(field => {
        const errorKey = `juridicalDocumentation.${field}`
        if (this.validationErrors[errorKey]) {
          delete this.validationErrors[errorKey]
        }
      })
    },

    updateEconomicOperationalData(newData) {
      this.formData.economicOperationalData = { ...this.formData.economicOperationalData, ...newData }
      // Limpar erros relacionados aos campos atualizados
      Object.keys(newData).forEach(field => {
        const errorKey = `economicOperationalData.${field}`
        if (this.validationErrors[errorKey]) {
          delete this.validationErrors[errorKey]
        }
      })
    },

    updateLinkedInstitutions(newData) {
      console.log('CredentialingPublic - updateLinkedInstitutions chamado com:', newData);

      if (newData && Array.isArray(newData.linked_institutions)) {
        this.formData.linkedInstitutions = newData.linked_institutions;
        console.log('CredentialingPublic - linkedInstitutions atualizado:', this.formData.linkedInstitutions);
      } else {
        console.log('CredentialingPublic - dados inválidos recebidos em updateLinkedInstitutions');
      }

      // Limpar erros relacionados
      Object.keys(this.validationErrors).forEach(errorKey => {
        if (errorKey.startsWith('linkedInstitutions.')) {
          delete this.validationErrors[errorKey];
        }
      });
    },

    updateAdditionalAttachments(data) {
      if (data.additional_attachments) {
        this.formData.additionalAttachments = {
          ...this.formData.additionalAttachments,
          additional_attachments: data.additional_attachments
        };
      }
    },

    updateAcceptTerms(value) {
      this.formData.acceptTerms = value
      if (value && this.validationErrors.acceptTerms) {
        delete this.validationErrors.acceptTerms
      }
    },

    // Envio do formulário
    async submitCredentialingForm() {
      // Bloqueia envio se formulário está desabilitado
      if (this.isFormDisabled) {
        console.log('🔒 Formulário desabilitado - envio bloqueado');
        this.$vs.notify({
          title: 'Formulário Finalizado',
          text: 'Este formulário já foi processado e não pode ser enviado novamente.',
          color: 'warning',
          position: 'top-center'
        });
        return;
      }

      // Validação final
      if (!this.formData.acceptTerms) {
        this.validationErrors['acceptTerms'] = true
        this.notifyError(this.$vs, 'Você deve aceitar os termos e condições.')
        return;
      }

      try {
        this.$vs.loading({
          text: 'Salvando e enviando solicitação...'
        });

        // Salvar dados finais antes de enviar
        const response = await this.saveFormData('final_submission', false);
        console.log('CredentialingPublic - response:', response);
        this.protocolNumber = response.protocol_number;

        this.$vs.loading.close();
        this.showSuccessModal = true;

      } catch (error) {
        console.error('Erro ao enviar solicitação:', error);
        this.$vs.loading.close();
      }finally{
        this.$vs.loading.close();
      }
    },

    resetForm() {
      this.credentialingId = null;
      this.formData = {
        id: null,
        process_code: null,
        status: null,
        basicData: {
          credentialing_type: '',
          activity_area: '',
          entity_name: '',
          cnpj: '',
          main_cnae: '',
          address: '',
          phone: '',
          email: '',
          legal_representative_name: '',
          legal_representative_cpf: '',
          legal_representative_position: '',
          legal_representative_email: ''
        },
        juridicalDocumentation: {
          statute: null,
          election_minutes: null,
          foundation_document: null,
          cnpj_certificate: null,
          identification_members: null
        },
        economicOperationalData: {
          certificate_period_start: null,
          certificate_period_end: null,
          scholarship_count: '',
          scholarship_types: [],
          support_services: '',
          report_activities: null,
          summary_report_scholarships: null,
          list_scholarships: null,
          analysis_socioeconomic_students: null,
          accounting_regime: null,
          accounting_reports: null,
          accounting_gratuity_demo: null,
          audit_opinion: null,
          revenue_received: null,
          service_plan: null,
          social_assistance_registration: null
        },
        linkedInstitutions: [],
        additionalAttachments: {
          additional_attachments: []
        },
        acceptTerms: false
      };
      this.formStarted = false;
      this.initialAcceptance = {
        dataVeracity: false,
        dataCollection: false
      };
      this.currentStep = null;
      this.isFormCompleted = false;
      this.validationErrors = {};
      this.errors = {};
      this.saveErrors = {};
      this.isSaving = false;
      this.lastSaveTime = null;
    },

    // Métodos existentes
    getCredentialingByLink(){
      this.$vs.loading()
      this.service.getCredentialingByLink(this.link).then(
        data => {
          console.log('CredentialingPublic - Initial data loaded:', data);
          this.credentialingId = data.id;
          this.portalCebasConfig = {
            id: data.id,
            title_page: data.title_page || '',
            link: data.link || '',
            info_page: data.info_page || '',
            color: data.color || ''
          };

          // Atualiza o status do formulário se disponível
          if (data.status) {
            this.formData.status = data.status;
            console.log('CredentialingPublic - Status carregado:', data.status);
          }

          // Atualiza os dados do formulário
          if (data.steps_data) {
            console.log('CredentialingPublic - steps_data encontrado:', data.steps_data);
            console.log('CredentialingPublic - linked_institutions original:', data.steps_data.linked_institutions);

            // Mapeia a documentação jurídica com status de uploaded correto
            const juridicalDocs = data.steps_data.juridical_documentation || {};
            const mappedJuridicalDocs = {};

            Object.keys(juridicalDocs).forEach(docKey => {
              if (juridicalDocs[docKey] && typeof juridicalDocs[docKey] === 'object') {
                mappedJuridicalDocs[docKey] = {
                  ...juridicalDocs[docKey],
                  uploaded: true,
                  metadata: {
                    original_name: juridicalDocs[docKey].original_name || juridicalDocs[docKey].name || juridicalDocs[docKey].file_name,
                    size: juridicalDocs[docKey].size,
                    type: juridicalDocs[docKey].type || juridicalDocs[docKey].mime_type,
                    document_type: docKey
                  }
                };
              }
            });

            // Mapeia os dados econômicos operacionais com status de uploaded correto
            const economicData = data.steps_data.economic_operational_data || {};
            const mappedEconomicData = { ...economicData };

                         // Mapeia especificamente os documentos econômicos para ter a estrutura correta
             const economicDocFields = ['report_activities', 'summary_report_scholarships', 'list_scholarships', 'analysis_socioeconomic_students', 'accounting_regime', 'accounting_reports', 'accounting_gratuity_demo', 'audit_opinion', 'revenue_received', 'service_plan', 'social_assistance_registration'];
            economicDocFields.forEach(docKey => {
              if (economicData[docKey] && typeof economicData[docKey] === 'object') {
                mappedEconomicData[docKey] = {
                  ...economicData[docKey],
                  uploaded: true,
                  metadata: {
                    original_name: economicData[docKey].original_name || economicData[docKey].name || economicData[docKey].file_name,
                    size: economicData[docKey].size,
                    type: economicData[docKey].type || economicData[docKey].mime_type,
                    document_type: docKey
                  }
                };
              }
            });

            this.formData = {
              basicData: data.steps_data.basic_data || {},
              juridicalDocumentation: mappedJuridicalDocs,
              economicOperationalData: mappedEconomicData,
              linkedInstitutions: Array.isArray(data.steps_data.linked_institutions) ? data.steps_data.linked_institutions : [],
              additionalAttachments: {
                additional_attachments: Array.isArray(data.steps_data.additional_attachments)
                  ? data.steps_data.additional_attachments.map(attachment => ({
                      ...attachment,
                      uploaded: true,
                      metadata: {
                        original_name: attachment.original_name || attachment.name,
                        size: attachment.size,
                        type: attachment.type,
                        document_type: 'additional_attachment'
                      }
                    }))
                  : []
              }
            }

            console.log('CredentialingPublic - formData atualizado:', this.formData);
            console.log('CredentialingPublic - additionalAttachments após atualização:', this.formData.additionalAttachments);
          } else {
            console.log('CredentialingPublic - steps_data não encontrado');
          }

          this.$vs.loading.close()

          // Verifica posição do badge após dados carregados
          this.$nextTick(() => {
            setTimeout(() => {
              if (!this.originalBadgePosition) {
                console.log('Capturando posição do badge após dados carregados...');
                this.checkFloatingBadgePosition();
              }
            }, 200);
          });
        },
        error => {
          console.error('Error loading credentialing:', error)
          this.$vs.loading.close()
        }
      )
    },

    showStartCredentialingModal() {
      // Limpar dados antes de abrir o modal
      this.resetStartModal();
      this.showStartModal = true;
    },

    backToInitialScreen() {
      // Reset completo do formulário
      this.resetForm();
      this.formStarted = false;
    },

    getInitialFieldState(field) {
      if (this.initialAcceptance[field]) {
        return null
      }
      return false
    },

    // Métodos para funcionalidade "Continuar Credenciamento"
    showContinueCredentialingModal() {
      // Limpar dados antes de abrir o modal
      this.resetContinueModal();
      this.showContinueModal = true;
    },

    resetContinueModal() {
      this.continueEmail = '';
      this.continueEmailValidation = null;
      this.continueCode = '';
      this.continueCodeValidation = null;
      this.searchingCredentialing = false;
    },

    closeContinueModal() {
      this.showContinueModal = false;
      // Limpar dados ao fechar
      this.resetContinueModal();
      this.scrollToTop();
    },

    validateContinueEmail() {
      if (!this.continueEmail) {
        this.continueEmailValidation = null;
      } else if (this.isValidEmail(this.continueEmail)) {
        this.continueEmailValidation = true;
      } else {
        this.continueEmailValidation = false;
      }
    },

    validateContinueCode() {
      if (!this.continueCode) {
        this.continueCodeValidation = null;
      } else if (this.continueCode.length === 6) {
        this.continueCodeValidation = true;
      } else {
        this.continueCodeValidation = false;
      }
    },

    validateStartEmail() {
      // Se havia um código enviado anteriormente, resetar
      if (this.codeSent) {
        this.codeSent = false;
        this.generatedCode = null;
        this.sendingCode = false;
      }

      // Limpar dados do formulário se necessário
      this.clearFormEmailIfNeeded();

      if (!this.startEmail) {
        this.startEmailValidation = null;
      } else if (this.isValidEmail(this.startEmail)) {
        this.startEmailValidation = true;
      } else {
        this.startEmailValidation = false;
      }
    },

    resetStartModal() {
      this.startEmail = '';
      this.startEmailValidation = null;
      this.sendingCode = false;
      this.codeSent = false;
      this.generatedCode = null;
    },

    closeStartModal() {
      this.showStartModal = false;
      // Limpar dados ao fechar
      this.resetStartModal();
    },

    async sendVerificationCode() {
      if (!this.isStartEmailValid) {
        return;
      }

      this.sendingCode = true;

      try {
        // Simular envio de e-mail (substitua pela chamada real da API)
        //await this.simulateEmailSending();

        // Em produção seria algo como:
        await this.service.initForm(this.link, this.startEmail).then(
          (response) => {
            this.startCredentialingProcess();

            this.setFormDataBasicData(response);

            this.$vs.notify({
              title: 'Sucesso!',
              text: 'Credenciamento iniciado com sucesso!',
              color: 'success'
            });
          }
        );
      } catch (error) {
        console.error('Erro ao enviar código:', error);
        /*
        this.$vs.notify({
          title: 'Erro',
          text: 'Ocorreu algum erro ao iniciar o processo de credenciamento. Tente novamente.',
          color: 'danger',
          position: 'top-center'
        });
        */
      } finally {
        this.sendingCode = false;
      }
    },

    setFormDataBasicData(response) {
      this.credentialingId = response.id;
      this.formData.id = response.id;
      this.formData.basicData.email = response.email
      this.formData.basicData.process_code = response.process_code
      this.formData.basicData.status = response.status
      this.formData.basicData.credentialing_type = response.credentialing_type
      this.formData.basicData.activity_area = response.activity_area
      this.formData.basicData.main_cnae = response.main_cnae
      //Legal Entity
      this.formData.basicData.entity_name = response.legal_entity.name
      this.formData.basicData.cnpj = response.legal_entity.cnpj_formmated
      this.formData.basicData.address = response.address
      this.formData.basicData.phone = response.phone
    },

    async simulateEmailSending() { this.codeSent = false;
      // Simular delay do envio de e-mail
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Gerar código de 6 dígitos
      this.generatedCode = Math.random().toString().substr(2, 6);
      this.codeSent = true;

      // Pré-preencher dados do formulário com o e-mail
      this.formData.basicData.email = this.startEmail;

      this.$vs.notify({
        title: 'Código enviado!',
        text: `Código de validação enviado para ${this.startEmail}`,
        color: 'success'
      });

      // EM PRODUÇÃO:
      // - Remova a linha que mostra o código gerado
      // - O código só deve estar disponível no e-mail do usuário
      // - Implemente a chamada real da API:
      // return this.service.sendVerificationCode(this.startEmail);
    },

    startCredentialingProcess() {
      this.showStartModal = false;
      this.resetStartModal();
      this.formStarted = true;
    },

    async searchExistingCredentialing() {
      if (!this.canSearchCredentialing) {
        return;
      }

      this.searchingCredentialing = true;

      try {
        // Simular busca na API (substitua pela chamada real)
        //await this.simulateApiSearch();

        // Em caso real, seria algo como:
         const response = await this.service.getCredentialingByEmailAndCode(this.link, this.continueEmail, this.continueCode);
        this.loadExistingCredentialingData(response);
        this.closeContinueModal();
        this.startCredentialingProcess();
        this.$vs.notify({
          title: 'Sucesso!',
          text: 'Solicitação de credenciamento encontrada. Carregando os dados...',
          color: 'success',
          position: 'top-center',
          time: 4000,
          icon: 'feather icon-check-circle'
        });

      } catch (error) {
        console.error('Erro ao buscar credenciamento:', error);
      } finally {
        this.searchingCredentialing = false;
      }
    },

    async simulateApiSearch() {
      // Simular delay da API
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simular diferentes cenários baseados no e-mail e código
      // PARA IMPLEMENTAR COM API REAL:
      // 1. Substitua este método por uma chamada real à API
      // 2. Use this.service.getCredentialingByEmailAndCode(this.continueEmail, this.continueCode)
      // 3. Trate os casos: encontrado, não encontrado, erro, código inválido

      if (this.continueEmail === 'teste@exemplo.com' && this.continueCode === '123456') {
        // Simular credenciamento encontrado com código correto
        this.loadExistingCredentialingData({
          basicData: {
            credentialing_type: 'credenciamento',
            activity_area: 'health',
            entity_name: 'ONG Exemplo',
            cnpj: '12.345.678/0001-90',
            main_cnae: '',
            address: 'Rua Exemplo, 123 - São Paulo, SP',
            phone: '(11) 99999-9999',
            email: 'teste@exemplo.com',
            legal_representative_name: 'João Silva',
            legal_representative_cpf: '123.456.789-00',
            legal_representative_position: 'Coordenador',
            legal_representative_email: 'joao.silva@exemplo.com'
          },
          juridicalDocumentation: {
            statute: null,
            election_minutes: null,
            foundation_document: null,
            cnpj_certificate: null,
            identification_members: null
          },
          economicOperationalData: {
            certificate_period_start: null,
            certificate_period_end: null,
            scholarship_count: '',
            scholarship_types: [],
            support_services: ''
          },
          linkedInstitutions: {
            institution_id: '',
            institution_name: '',
            institution_cnpj: '',
            city_id: '',
            city_name: '',
            state_id: '',
            state_name: '',
            state_short: '',
            institution_authorization: null,
            school_regulations: null
          },
          additionalAttachments: {
            additional_attachments: []
          },
          acceptTerms: false
        });

        this.showContinueModal = false;
        this.resetContinueModal();
        this.startCredentialingProcess();

        this.$vs.notify({
          title: 'Sucesso!',
          text: 'Credenciamento encontrado. Dados carregados com sucesso!',
          color: 'success'
        });
      } else if (this.continueEmail === 'teste@exemplo.com' && this.continueCode !== '123456') {
        // Simular código incorreto
        this.$vs.notify({
          title: 'Código incorreto',
          text: 'O código de credenciamento informado não confere. Verifique o código recebido por e-mail.',
          color: 'warning'
        });
      } else {
        // Simular credenciamento não encontrado
        this.$vs.notify({
          title: 'Não encontrado',
          text: 'Nenhum credenciamento foi encontrado com este e-mail e código. Verifique os dados ou inicie um novo credenciamento.',
          color: 'warning'
        });
      }
    },

    loadExistingCredentialingData(data) {
      // Atualiza o ID do credenciamento
      this.credentialingId = data.id;

      // Atualiza o status
      if (data.status) {
        this.formData.status = data.status;
      }

      // Carrega os dados dos steps
      if (data.steps_data) {
        // Garante que os dados básicos estejam corretos
        if (data.steps_data.basic_data) {
          // Preserva os valores existentes
          const currentBasicData = this.formData.basicData || {};

          // Atualiza com os novos dados
          this.formData.basicData = {
            ...currentBasicData,
            ...data.steps_data.basic_data,
            // Garante que os valores específicos sejam mantidos
            credentialing_type: data.steps_data.basic_data.credentialing_type || currentBasicData.credentialing_type || null,
            activity_area: data.steps_data.basic_data.activity_area || currentBasicData.activity_area || null
          };
        }

        // Mapeia a documentação jurídica com status de uploaded correto
        const juridicalDocs = data.steps_data.juridical_documentation || {};
        this.formData.juridicalDocumentation = {};

        // Mapeia cada documento jurídico para ter a estrutura correta
        Object.keys(juridicalDocs).forEach(docKey => {
          if (juridicalDocs[docKey] && typeof juridicalDocs[docKey] === 'object') {
            this.formData.juridicalDocumentation[docKey] = {
              ...juridicalDocs[docKey],
              uploaded: true,
              metadata: {
                original_name: juridicalDocs[docKey].original_name || juridicalDocs[docKey].name || juridicalDocs[docKey].file_name,
                size: juridicalDocs[docKey].size,
                type: juridicalDocs[docKey].type || juridicalDocs[docKey].mime_type,
                document_type: docKey
              }
            };
          }
        });

        console.log('Dados carregados - juridicalDocumentation:', this.formData.juridicalDocumentation);

        // Mapeia os dados econômicos operacionais com status de uploaded correto
        const economicData = data.steps_data.economic_operational_data || {};
        this.formData.economicOperationalData = { ...economicData };

        // Mapeia especificamente os documentos econômicos para ter a estrutura correta
        const economicDocFields = ['report_activities', 'summary_report_scholarships', 'list_scholarships', 'analysis_socioeconomic_students', 'accounting_regime', 'accounting_reports', 'accounting_gratuity_demo', 'audit_opinion', 'revenue_received', 'service_plan', 'social_assistance_registration'];
        economicDocFields.forEach(docKey => {
          if (economicData[docKey] && typeof economicData[docKey] === 'object') {
            this.formData.economicOperationalData[docKey] = {
              ...economicData[docKey],
              uploaded: true,
              metadata: {
                original_name: economicData[docKey].original_name || economicData[docKey].name || economicData[docKey].file_name,
                size: economicData[docKey].size,
                type: economicData[docKey].type || economicData[docKey].mime_type,
                document_type: docKey
              }
            };
          }
        });

        console.log('Dados carregados - economicOperationalData:', this.formData.economicOperationalData);
        this.formData.linkedInstitutions = Array.isArray(data.steps_data.linked_institutions) ? data.steps_data.linked_institutions : [];

        // Atualiza os anexos complementares com a estrutura correta
        const additionalAttachments = data.steps_data.additional_attachments || [];
        this.formData.additionalAttachments = {
          additional_attachments: Array.isArray(additionalAttachments)
            ? additionalAttachments.map(attachment => ({
                ...attachment,
                uploaded: true,
                metadata: {
                  original_name: attachment.original_name || attachment.name,
                  size: attachment.size,
                  type: attachment.type,
                  document_type: 'additional_attachment'
                }
              }))
            : []
        };

        console.log('Dados carregados - additionalAttachments:', this.formData.additionalAttachments);
      }

      // Atualiza o estado do formulário
      if (data.form_state) {
        this.currentStep = data.form_state.current_step;
        this.isFormCompleted = data.form_state.is_completed;
      }
    },

    // Métodos auxiliares para cores
    setGlobalCSSVariables(styles) {
      const root = document.documentElement;
      Object.keys(styles).forEach(property => {
        root.style.setProperty(property, styles[property]);
      });
    },

    isSystemColorName(colorName) {
      const systemColors = [
        'primary', 'secondary', 'success', 'danger', 'warning',
        'info', 'light', 'dark', 'muted'
      ];
      return systemColors.includes(colorName?.toLowerCase());
    },

    getSystemColor(colorName) {
      const root = getComputedStyle(document.documentElement);
      const normalizedName = colorName.toLowerCase();

      // Mapeia nomes de cores para possíveis variáveis CSS
      const colorVarMap = {
        'primary': ['--bs-primary', '--primary', '--vs-primary'],
        'secondary': ['--bs-secondary', '--secondary', '--vs-secondary'],
        'success': ['--bs-success', '--success', '--vs-success'],
        'danger': ['--bs-danger', '--danger', '--vs-danger'],
        'warning': ['--bs-warning', '--warning', '--vs-warning'],
        'info': ['--bs-info', '--info', '--vs-info'],
        'light': ['--bs-light', '--light', '--vs-light'],
        'dark': ['--bs-dark', '--dark', '--vs-dark'],
        'muted': ['--bs-muted', '--muted', '--vs-muted']
      };

      const possibleVars = colorVarMap[normalizedName] || [];

      for (const varName of possibleVars) {
        const color = root.getPropertyValue(varName).trim();
        if (color && color !== '') {
          return color;
        }
      }

      // Fallback para cores padrão conhecidas
      const defaultColors = {
        'primary': '#156440',    // $green - valor do projeto
        'secondary': '#82868b',  // valor do projeto
        'success': '#28c76f',    // $success - valor do vuexy
        'danger': '#ea5455',     // $red/$danger - valor do projeto
        'warning': '#ff9f43',    // $orange/$warning - valor do projeto
        'info': '#00cfe8',       // $blue/$info - valor do projeto
        'light': '#f6f6f6',      // $gray-50 - valor do projeto
        'dark': '#4b4b4b',       // valor do projeto
        'muted': '#636363'       // $gray-400 - valor do projeto
      };

      return defaultColors[normalizedName] || getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim() || '#156440';
    },

    // Métodos auxiliares para manipulação de cores
    hexToRgb(hex) {
      // Converte hex para RGB
      if (!hex || !hex.startsWith('#')) return '21, 100, 64'; // Fallback

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ?
        `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` :
        '21, 100, 64';
    },

    lightenColor(color, amount) {
      // Simplificado - para cores hex
      if (color.startsWith('#')) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * amount * 100);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
          (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
          (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
      }
      return color; // Retorna a cor original se não for hex
    },

    darkenColor(color, amount) {
      // Simplificado - para cores hex
      if (color.startsWith('#')) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * amount * 100);
        const R = (num >> 16) - amt;
        const G = (num >> 8 & 0x00FF) - amt;
        const B = (num & 0x0000FF) - amt;
        return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
          (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
          (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
      }
      return color; // Retorna a cor original se não for hex
    },

    resetVerificationData() {
      this.sendingCode = false;
      this.codeSent = false;
      this.generatedCode = null;

      // Limpar e-mail do formulário se estava preenchido anteriormente
      if (this.formData.basicData.email && this.formData.basicData.email !== this.startEmail) {
        this.formData.basicData.email = '';
      }
    },

    clearFormEmailIfNeeded() {
      // Se o e-mail do formulário não corresponde ao e-mail atual, limpar
      if (this.formData.basicData.email && this.formData.basicData.email !== this.startEmail) {
        this.formData.basicData.email = '';
      }
    },

    // ========================================
    // MÉTODOS DE DESENVOLVIMENTO - MOCK DATA
    // ========================================

    /**
     * Cria um arquivo mock para simulação em desenvolvimento
     */
    createMockFile(name, type = 'application/pdf', content = 'Mock file content') {
      if (!this.isDevelopment) return null;

      // Criar um Blob simulando um arquivo
      const blob = new Blob([content], { type });

      // Criar File a partir do Blob
      const file = new File([blob], name, {
        type,
        lastModified: Date.now()
      });

      return file;
    },

    /**
     * Função principal para preencher todos os dados com mocks
     */
    async fillWithMockData() {
      if (!this.isDevelopment) {
        console.warn('Mock data só funciona em ambiente de desenvolvimento');
        return;
      }

      this.$vs.loading({
        text: 'Preenchendo com dados de teste...'
      });

      try {
        // Aguardar um pouco para dar feedback visual
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Preencher todos os steps
        this.fillBasicDataMock();
        this.fillJuridicalDocumentationMock();
        this.fillEconomicOperationalDataMock();
        this.fillLinkedInstitutionsMock();
        this.fillAdditionalAttachmentsMock();

        // Limpar erros de validação
        this.validationErrors = {};

        // Marcar aceitações iniciais
        this.initialAcceptance.dataVeracity = true;
        this.initialAcceptance.dataCollection = true;

        this.$vs.notify({
          title: 'Mock Data Carregado!',
          text: 'Todos os formulários foram preenchidos com dados de teste.',
          color: 'success',
          position: 'top-center',
          time: 4000,
          icon: 'feather icon-check-circle'
        });

      } catch (error) {
        console.error('Erro ao carregar mock data:', error);
        this.$vs.notify({
          title: 'Erro',
          text: 'Erro ao carregar dados de teste.',
          color: 'danger'
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    /**
     * Preenche dados básicos com mock
     */
    fillBasicDataMock() {
      const mockBasicData = {
        credentialing_type: 'credenciamento',
        activity_area: 'health',
        entity_name: 'Instituto Exemplo de Desenvolvimento Social',
        cnpj: '12.345.678/0001-90',
        main_cnae: '8899-5/00',
        address: 'Rua das Flores, 123, Centro - São Paulo/SP - CEP: 01234-567',
        phone: '(11) 3333-4444',
        email: 'contato@instituto-exemplo.org.br',
        legal_representative_name: 'Maria José da Silva',
        legal_representative_cpf: '123.456.789-00',
        legal_representative_position: 'Presidente',
        legal_representative_email: 'maria.silva@instituto-exemplo.org.br'
      };

      this.updateBasicData(mockBasicData);
    },

    /**
     * Preenche documentação jurídica com arquivos mock
     */
    fillJuridicalDocumentationMock() {
      const mockJuridicalData = {
        statute: this.createMockFile('estatuto_social.pdf', 'application/pdf'),
        election_minutes: this.createMockFile('ata_eleicao.pdf', 'application/pdf'),
        foundation_document: this.createMockFile('documento_fundacao.pdf', 'application/pdf'),
        cnpj_certificate: this.createMockFile('certidao_cnpj.pdf', 'application/pdf'),
        identification_members: this.createMockFile('identification_members.pdf', 'application/pdf')
      };

      this.updateJuridicalDocumentation(mockJuridicalData);
    },

    /**
     * Preenche dados econômicos e operacionais com mock
     */
    fillEconomicOperationalDataMock() {
      const currentDate = new Date();
      const startDate = new Date(currentDate.getFullYear() - 1, 0, 1); // Janeiro do ano passado
      const endDate = new Date(currentDate.getFullYear(), 11, 31); // Dezembro deste ano

      // Criar arquivos mock com estrutura correta
      const reportActivitiesFile = this.createMockFile('relatorio_atividades_desempenhadas.pdf', 'application/pdf');
      const summaryReportFile = this.createMockFile('relatorio_sintetico_bolsas.pdf', 'application/pdf');
      const listScholarshipsFile = this.createMockFile('relacao_bolsas_estudo.pdf', 'application/pdf');
      const analysisSocioeconomicFile = this.createMockFile('analise_perfil_socioeconomico_alunos.pdf', 'application/pdf');
      const accountingRegimeFile = this.createMockFile('regime_contabil.pdf', 'application/pdf');
      const accountingReportsFile = this.createMockFile('relatorios_contabeis.pdf', 'application/pdf');
      const accountingGratuityDemoFile = this.createMockFile('demonstracao_contabil_gratuidade.pdf', 'application/pdf');
      const auditOpinionFile = this.createMockFile('parecer_auditoria_independente.pdf', 'application/pdf');
      const revenueReceivedFile = this.createMockFile('receita_efetivamente_recebida.pdf', 'application/pdf');
      const servicePlanFile = this.createMockFile('plano_de_atendimento.pdf', 'application/pdf');
      const socialAssistanceRegistrationFile = this.createMockFile('inscricao_servicos_socioassistenciais.pdf', 'application/pdf');

      const mockEconomicData = {
        certificate_period_start: startDate.toISOString().split('T')[0],
        certificate_period_end: endDate.toISOString().split('T')[0],
        scholarship_count: '150',
        scholarship_types: 'BO', // Corrigido para usar a estrutura esperada pela API
        support_services: 'O instituto oferece serviços de apoio educacional, incluindo reforço escolar, orientação vocacional, e atividades extracurriculares para jovens em situação de vulnerabilidade social.',
        report_activities: {
          file: reportActivitiesFile,
          uploaded: true,
          file_name: 'relatorio_atividades_desempenhadas.pdf',
          original_name: 'relatorio_atividades_desempenhadas.pdf',
          size: reportActivitiesFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-report-activities',
          metadata: {
            original_name: 'relatorio_atividades_desempenhadas.pdf',
            size: reportActivitiesFile.size,
            type: 'application/pdf',
            document_type: 'report_activities'
          }
        },
        summary_report_scholarships: {
          file: summaryReportFile,
          uploaded: true,
          file_name: 'relatorio_sintetico_bolsas.pdf',
          original_name: 'relatorio_sintetico_bolsas.pdf',
          size: summaryReportFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-summary-report',
          metadata: {
            original_name: 'relatorio_sintetico_bolsas.pdf',
            size: summaryReportFile.size,
            type: 'application/pdf',
            document_type: 'summary_report_scholarships'
          }
        },
        list_scholarships: {
          file: listScholarshipsFile,
          uploaded: true,
          file_name: 'relacao_bolsas_estudo.pdf',
          original_name: 'relacao_bolsas_estudo.pdf',
          size: listScholarshipsFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-list-scholarships',
          metadata: {
            original_name: 'relacao_bolsas_estudo.pdf',
            size: listScholarshipsFile.size,
            type: 'application/pdf',
            document_type: 'list_scholarships'
          }
        },
        analysis_socioeconomic_students: {
          file: analysisSocioeconomicFile,
          uploaded: true,
          file_name: 'analise_perfil_socioeconomico_alunos.pdf',
          original_name: 'analise_perfil_socioeconomico_alunos.pdf',
          size: analysisSocioeconomicFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-analysis-socioeconomic',
          metadata: {
            original_name: 'analise_perfil_socioeconomico_alunos.pdf',
            size: analysisSocioeconomicFile.size,
            type: 'application/pdf',
            document_type: 'analysis_socioeconomic_students'
          }
        },
        accounting_regime: {
          file: accountingRegimeFile,
          uploaded: true,
          file_name: 'regime_contabil.pdf',
          original_name: 'regime_contabil.pdf',
          size: accountingRegimeFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-accounting-regime',
          metadata: {
            original_name: 'regime_contabil.pdf',
            size: accountingRegimeFile.size,
            type: 'application/pdf',
            document_type: 'accounting_regime'
          }
        },
        accounting_reports: {
          file: accountingReportsFile,
          uploaded: true,
          file_name: 'relatorios_contabeis.pdf',
          original_name: 'relatorios_contabeis.pdf',
          size: accountingReportsFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-accounting-reports',
          metadata: {
            original_name: 'relatorios_contabeis.pdf',
            size: accountingReportsFile.size,
            type: 'application/pdf',
            document_type: 'accounting_reports'
          }
        },
        accounting_gratuity_demo: {
          file: accountingGratuityDemoFile,
          uploaded: true,
          file_name: 'demonstracao_contabil_gratuidade.pdf',
          original_name: 'demonstracao_contabil_gratuidade.pdf',
          size: accountingGratuityDemoFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-accounting-gratuity-demo',
          metadata: {
            original_name: 'demonstracao_contabil_gratuidade.pdf',
            size: accountingGratuityDemoFile.size,
            type: 'application/pdf',
            document_type: 'accounting_gratuity_demo'
          }
        },
        audit_opinion: {
          file: auditOpinionFile,
          uploaded: true,
          file_name: 'parecer_auditoria_independente.pdf',
          original_name: 'parecer_auditoria_independente.pdf',
          size: auditOpinionFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-audit-opinion',
          metadata: {
            original_name: 'parecer_auditoria_independente.pdf',
            size: auditOpinionFile.size,
            type: 'application/pdf',
            document_type: 'audit_opinion'
          }
        },
        revenue_received: {
          file: revenueReceivedFile,
          uploaded: true,
          file_name: 'receita_efetivamente_recebida.pdf',
          original_name: 'receita_efetivamente_recebida.pdf',
          size: revenueReceivedFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-revenue-received',
          metadata: {
            original_name: 'receita_efetivamente_recebida.pdf',
            size: revenueReceivedFile.size,
            type: 'application/pdf',
            document_type: 'revenue_received'
          }
        },
        service_plan: {
          file: servicePlanFile,
          uploaded: true,
          file_name: 'plano_de_atendimento.pdf',
          original_name: 'plano_de_atendimento.pdf',
          size: servicePlanFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-service-plan',
          metadata: {
            original_name: 'plano_de_atendimento.pdf',
            size: servicePlanFile.size,
            type: 'application/pdf',
            document_type: 'service_plan'
          }
        },
        social_assistance_registration: {
          file: socialAssistanceRegistrationFile,
          uploaded: true,
          file_name: 'inscricao_servicos_socioassistenciais.pdf',
          original_name: 'inscricao_servicos_socioassistenciais.pdf',
          size: socialAssistanceRegistrationFile.size,
          type: 'application/pdf',
          url_tmp: '#mock-url-social-assistance-registration',
          metadata: {
            original_name: 'inscricao_servicos_socioassistenciais.pdf',
            size: socialAssistanceRegistrationFile.size,
            type: 'application/pdf',
            document_type: 'social_assistance_registration'
          }
        }
      };

      // Debug log
      if (this.isDevelopment) {
        console.log('Mock Economic Data:', mockEconomicData);
        console.log('Current Economic Data before update:', this.formData.economicOperationalData);
      }

      this.updateEconomicOperationalData(mockEconomicData);

      // Debug log after update
      if (this.isDevelopment) {
        console.log('Current Economic Data after update:', this.formData.economicOperationalData);
      }
    },

    /**
     * Preenche unidades vinculadas com mock
     */
    fillLinkedInstitutionsMock() {
      const mockLinkedData = {
        institution_id: 1,
        institution_name: 'Escola Técnica Associada',
        institution_cnpj: '98.765.432/0001-10',
        city_id: 1,
        city_name: 'São Paulo',
        state_id: 1,
        state_name: 'SP',
        state_short: 'SP',
        institution_authorization: this.createMockFile('autorizacao_unidade.pdf', 'application/pdf'),
        school_regulations: this.createMockFile('regimento_interno.pdf', 'application/pdf')
      };

      this.updateLinkedInstitutions(mockLinkedData);
    },

    /**
     * Preenche anexos complementares com arquivos mock
     */
    fillAdditionalAttachmentsMock() {
      const mockAttachments = [
        this.createMockFile('relatorio_atividades_2023.pdf', 'application/pdf'),
        this.createMockFile('planilha_beneficiarios.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
        this.createMockFile('fotos_atividades.jpg', 'image/jpeg'),
        this.createMockFile('parecer_tecnico.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
      ];

      const mockAdditionalData = {
        additional_attachments: mockAttachments
      };

      this.updateAdditionalAttachments(mockAdditionalData);
    },

    /**
     * MÉTODO DE DESENVOLVIMENTO - Demonstra o comportamento do computed infoPageContent
     */
    testInfoPageBehavior() {
      if (!this.isDevelopment) return;

      // Alternar entre conteúdo padrão e personalizado
      if (!this.portalCebasConfig.info_page || this.portalCebasConfig.info_page === '') {
        // Definir conteúdo personalizado da API
        this.portalCebasConfig.info_page = `
          <div class="custom-info-page">
            <h3 class="text-success mb-3">🏥 Credenciamento CEBAS - Conteúdo da API</h3>
            <p class="lead">Este é um conteúdo <strong>personalizado</strong> vindo da API.</p>
            <div class="alert alert-success">
              <h6>✅ Conteúdo Customizado Detectado</h6>
              <p class="mb-0">O sistema está usando o conteúdo fornecido pela API em vez do padrão.</p>
            </div>
            <p>Para mais informações, acesse: <a href="https://www.gov.br/saude" target="_blank">Portal da Saúde</a></p>
          </div>
        `;

        this.$vs.notify({
          title: 'Teste API',
          text: 'Agora usando conteúdo personalizado da API',
          color: 'success'
        });
      } else {
        // Voltar para conteúdo padrão (limpar campo)
        this.portalCebasConfig.info_page = '';

        this.$vs.notify({
          title: 'Teste Padrão',
          text: 'Agora usando conteúdo padrão do computed',
          color: 'primary'
        });
      }
    },

    // ========================================
    // MÉTODOS DE SALVAMENTO AUTOMÁTICO - API
    // ========================================

    /**
     * MÉTODO DE DESENVOLVIMENTO - Debug dos dados para API
     */
    debugFormData() {
      if (!this.isDevelopment) return;

      const currentStep = this.getStepNameByIndex(this.getCurrentStepIndex());
      const apiData = this.prepareDataForAPI(currentStep);

      console.group('🔍 DEBUG - Dados para API');
      console.log('📊 Step Atual:', `${currentStep} (${this.getStepTitle(currentStep)})`);
      console.log('📤 Dados Completos:', apiData);
      console.log('🗂️ Dados por Step:', apiData.steps_data);
      console.log('⚙️ Estado do Form:', apiData.form_state);
      console.groupEnd();

      // Exibir modal com informações resumidas
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: '🔍 Debug - Dados da API',
        text: `
          <div style="text-align: left; font-family: monospace; font-size: 12px;">
            <strong>Step Atual:</strong> ${this.getStepTitle(currentStep)} (${currentStep})<br>
            <strong>Email:</strong> ${apiData.email}<br>
            <strong>Auto-Save:</strong> ${this.autoSaveEnabled ? '✅ Ativo' : '❌ Inativo'}<br>
            <strong>Último Save:</strong> ${this.lastSaveTime ? this.formatLastSaveTime() : 'Nunca'}<br>
            <strong>Steps Preenchidos:</strong><br>
            ${Object.keys(apiData.steps_data).map(step =>
              `&nbsp;&nbsp;• ${step}: ${Object.keys(apiData.steps_data[step]).length} campos`
            ).join('<br>')}
            <br><br>
            <small>Verifique o console para detalhes completos</small>
          </div>
        `,
        acceptText: 'Fechar',
        cancelText: null
      });
    },

    /**
     * MÉTODO DE DESENVOLVIMENTO - Toggle do auto-save
     */
    toggleAutoSave() {
      if (!this.isDevelopment) return;

      this.autoSaveEnabled = !this.autoSaveEnabled;

      this.$vs.notify({
        title: 'Auto-Save',
        text: `Auto-save ${this.autoSaveEnabled ? 'ativado' : 'desativado'}`,
        color: this.autoSaveEnabled ? 'success' : 'warning',
        position: 'bottom-right',
        time: 2000
      });

      console.log(`🔄 Auto-Save ${this.autoSaveEnabled ? 'ATIVADO' : 'DESATIVADO'}`);
    },

    /**
     * MÉTODO DE DESENVOLVIMENTO - Debug dos dados para API
     */
    debugFormData() {
      if (!this.isDevelopment) return;

      const currentStep = this.getStepNameByIndex(this.getCurrentStepIndex());
      const apiData = this.prepareDataForAPI(currentStep);

      console.group('🔍 DEBUG - Dados para API');
      console.log('📊 Step Atual:', `${currentStep} (${this.getStepTitle(currentStep)})`);
      console.log('📤 Dados Completos:', apiData);
      console.log('🗂️ Dados por Step:', apiData.steps_data);
      console.log('⚙️ Estado do Form:', apiData.form_state);
      console.groupEnd();

      // Exibir modal com informações resumidas
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: '🔍 Debug - Dados da API',
        text: `
          <div style="text-align: left; font-family: monospace; font-size: 12px;">
            <strong>Step Atual:</strong> ${this.getStepTitle(currentStep)} (${currentStep})<br>
            <strong>Email:</strong> ${apiData.email}<br>
            <strong>Auto-Save:</strong> ${this.autoSaveEnabled ? '✅ Ativo' : '❌ Inativo'}<br>
            <strong>Último Save:</strong> ${this.lastSaveTime ? this.formatLastSaveTime() : 'Nunca'}<br>
            <strong>Steps Preenchidos:</strong><br>
            ${Object.keys(apiData.steps_data).map(step =>
              `&nbsp;&nbsp;• ${step}: ${Object.keys(apiData.steps_data[step]).length} campos`
            ).join('<br>')}
            <br><br>
            <small>Verifique o console para detalhes completos</small>
          </div>
        `,
        acceptText: 'Fechar',
        cancelText: null
      });
    },

    /**
     * MÉTODO DE DESENVOLVIMENTO - Toggle do auto-save
     */
    toggleAutoSave() {
      if (!this.isDevelopment) return;

      this.autoSaveEnabled = !this.autoSaveEnabled;

      this.$vs.notify({
        title: 'Auto-Save',
        text: `Auto-save ${this.autoSaveEnabled ? 'ativado' : 'desativado'}`,
        color: this.autoSaveEnabled ? 'success' : 'warning',
        position: 'bottom-right',
        time: 2000
      });

      console.log(`🔄 Auto-Save ${this.autoSaveEnabled ? 'ATIVADO' : 'DESATIVADO'}`);
    },

    getStepIcon(step) {
      const icons = {
        basic_data: 'feather icon-user',
        juridical_documentation: 'feather icon-file-text',
        economic_operational_data: 'feather icon-bar-chart-2',
        linked_institutions: 'feather icon-link',
        additional_attachments: 'feather icon-paperclip',
        review: 'feather icon-check-circle',
        final_submission: 'feather icon-send'
      };
      return icons[step] || 'feather icon-file';
    },

    /**
     * Gerencia o upload de documentos
     * @param {Object} payload - Payload com fieldName e file
     * @param {string} payload.fieldName - Nome do campo (statute, election_minutes, etc)
     * @param {File} payload.file - Arquivo a ser enviado
     */
    async handleUploadJuridicalDocumentation({ fieldName, file, metadata, credentialingId }) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('credentialing_id', credentialingId ? credentialingId : this.credentialingId);
        formData.append('document_type', metadata.document_type);

        await this.service.uploadFile(this.link, formData).then((response) => {
          console.log('CredentialingPublic - API Response:', response);

          // Atualiza o status do arquivo no formData após upload bem-sucedido
          const updatedJuridicalDoc = {
            ...this.formData.juridicalDocumentation[fieldName],
            uploaded: true,
            file_id: response.file_id || response.id,
            metadata: {
              ...this.formData.juridicalDocumentation[fieldName].metadata,
              file_id: response.file_id || response.id
            }
          };

          // Atualiza o formData
          this.updateJuridicalDocumentation({
            [fieldName]: updatedJuridicalDoc
          });

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: `"${metadata.original_name}" foi enviado com sucesso.`,
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });

        })
      } catch (error) {
        console.error('Error uploading file:', error);
        this.$vs.notify({
          title: 'Erro no upload',
          text: `Não foi possível enviar o arquivo "${metadata.original_name}".`,
          color: 'danger'
        });
      }
    },

    async handleRemoveJuridicalDocumentation({ mediaId, documentId, index, metadata, fieldName }) {
      try {
        // Chama o serviço para remover o arquivo
        await this.service.deleteFile(this.link, this.credentialingId, documentId, mediaId).then(() => {
          // Remove da lista local apenas após sucesso da API
          if (fieldName) {
            this.formData.juridicalDocumentation[fieldName] = null;
          }

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: `"${metadata.original_name}" foi removido com sucesso.`,
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }).catch((error) => {
          console.error('Erro ao remover arquivo:', error);
          this.$vs.notify({
            title: 'Erro',
            text: 'Erro ao remover arquivo. Tente novamente.',
            color: 'danger',
            position: 'top-right'
          });
        });
      } catch (error) {
        console.error('Erro ao remover arquivo:', error);
      }
    },

    /**
     * Gerencia o upload de documentos
     * @param {Object} payload - Payload com fieldName e file
     * @param {string} payload.fieldName - Nome do campo (statute, election_minutes, etc)
     * @param {File} payload.file - Arquivo a ser enviado
     */
    async handleUploadAdditionalAttachments({ fieldName, file, metadata, credentialingId }) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('credentialing_id', credentialingId ? credentialingId : this.credentialingId);
        formData.append('document_type', metadata.document_type);

        const response = await this.service.uploadFile(this.link, formData);

        if (response.success) {
          // Update the file status in the form data
          const updatedAttachments = this.formData.additionalAttachments.additional_attachments.map(attachment => {
            if (attachment.metadata.original_name === metadata.original_name) {
              return {
                ...attachment,
                uploaded: true,
                file_id: response.file_id
              };
            }
            return attachment;
          });

          this.updateAdditionalAttachments({
            additional_attachments: updatedAttachments
          });

          this.$vs.notify({
            title: 'Upload concluído',
            text: `O arquivo "${metadata.original_name}" foi enviado com sucesso.`,
            color: 'success'
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        this.$vs.notify({
          title: 'Erro no upload',
          text: `Não foi possível enviar o arquivo "${metadata.original_name}".`,
          color: 'danger'
        });
      }
    },

    async handleRemoveAdditionalAttachments({ mediaId, documentId, index, metadata, fieldName }) {
      try {
        // Chama o serviço para remover o arquivo
        await this.service.deleteFile(this.link, this.credentialingId, documentId, mediaId).then(() => {
          // Remove da lista local apenas após sucesso da API
          if (fieldName) {
            // Para documentação jurídica
            this.formData.juridicalDocumentation[fieldName] = null;
          } else {
            // Para anexos complementares
            this.formData.additionalAttachments.additional_attachments.splice(index, 1);
          }

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: `"${metadata.original_name}" foi removido com sucesso.`,
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }).catch((error) => {
          console.error('Erro ao remover arquivo:', error);
          this.$vs.notify({
            title: 'Erro',
            text: 'Erro ao remover arquivo. Tente novamente.',
            color: 'danger',
            position: 'top-right'
          });
        });
      } catch (error) {
        console.error('Erro ao remover arquivo:', error);
      }
    },

    /**
     * Gerencia o upload de documentos econômicos operacionais
     * @param {Object} payload - Payload com fieldName e file
     * @param {string} payload.fieldName - Nome do campo (report_activities, summary_report_scholarships)
     * @param {File} payload.file - Arquivo a ser enviado
     */
    async handleUploadEconomicOperationalData({ fieldName, file, metadata, credentialingId }) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('credentialing_id', credentialingId ? credentialingId : this.credentialingId);
        formData.append('document_type', metadata.document_type);

        await this.service.uploadFile(this.link, formData).then((response) => {
          console.log('CredentialingPublic - API Response:', response);

          // Atualiza o status do arquivo no formData após upload bem-sucedido
          const updatedEconomicDoc = {
            ...this.formData.economicOperationalData[fieldName],
            uploaded: true,
            file_id: response.file_id || response.id,
            metadata: {
              ...this.formData.economicOperationalData[fieldName].metadata,
              file_id: response.file_id || response.id
            }
          };

          // Atualiza o formData
          this.updateEconomicOperationalData({
            [fieldName]: updatedEconomicDoc
          });

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: `"${metadata.original_name}" foi enviado com sucesso.`,
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });

        })
      } catch (error) {
        console.error('Error uploading file:', error);
        this.$vs.notify({
          title: 'Erro no upload',
          text: `Não foi possível enviar o arquivo "${metadata.original_name}".`,
          color: 'danger'
        });
      }
    },

    async handleRemoveEconomicOperationalData({ mediaId, documentId, index, metadata, fieldName }) {
      try {
        // Chama o serviço para remover o arquivo
        await this.service.deleteFile(this.link, this.credentialingId, documentId, mediaId).then(() => {
          // Remove da lista local apenas após sucesso da API
          if (fieldName) {
            this.formData.economicOperationalData[fieldName] = null;
          }

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: `"${metadata.original_name}" foi removido com sucesso.`,
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }).catch((error) => {
          console.error('Erro ao remover arquivo:', error);
          this.$vs.notify({
            title: 'Erro',
            text: 'Erro ao remover arquivo. Tente novamente.',
            color: 'danger',
            position: 'top-right'
          });
        });
      } catch (error) {
        console.error('Erro ao remover arquivo:', error);
      }
    },

    async handleAddInstitution(institutionData) {
      console.log('CredentialingPublic - EVENTO RECEBIDO - handleAddInstitution chamado');
      console.log('CredentialingPublic - institutionData recebido:', institutionData);
      console.log('CredentialingPublic - credentialingId:', this.credentialingId);
      console.log('CredentialingPublic - link:', this.link);

      try {
        console.log('CredentialingPublic - Adding institution with data:', institutionData);

        // Cria FormData a partir dos dados recebidos
        const formData = new FormData();
        formData.append('credentialing_id', this.credentialingId);
        formData.append('step', 'linked_institutions');
        formData.append('institution_name', institutionData.institution_name);
        formData.append('institution_cnpj', institutionData.institution_cnpj);
        formData.append('city_id', institutionData.city_id);
        formData.append('state_id', institutionData.state_id);

        // Adiciona os arquivos se existirem
        if (this.isFileObject(institutionData.institution_authorization)) {
          formData.append('institution_authorization', institutionData.institution_authorization);
        }
        if (this.isFileObject(institutionData.school_regulations)) {
          formData.append('school_regulations', institutionData.school_regulations);
        }

        console.log('CredentialingPublic - FormData prepared for API:', {
          credentialingId: this.credentialingId,
          institution_name: institutionData.institution_name,
          institution_cnpj: institutionData.institution_cnpj,
          city_id: institutionData.city_id,
          state_id: institutionData.state_id,
          hasAuthorizationFile: this.isFileObject(institutionData.institution_authorization),
          hasSchoolRegulationsFile: this.isFileObject(institutionData.school_regulations)
        });

        // Chama o serviço para adicionar a unidade, passando o link do credenciamento
        await this.service.addLinkedInstitution(this.link, formData).then((response) => {
          console.log('CredentialingPublic - API Response:', response);

          // Atualiza os dados locais com a resposta da API
          const institution = {
            institution_id: response.id,
            institution_name: institutionData.institution_name,
            institution_cnpj: institutionData.institution_cnpj,
            city_id: institutionData.city_id,
            city_name: institutionData.city_name,
            state_id: institutionData.state_id,
            state_name: institutionData.state_name,
            state_short: institutionData.state_short,
            institution_authorization: response.institution_authorization,
            school_regulations: response.school_regulations
          };

          console.log('CredentialingPublic - Mapped institution:', institution);

          // Atualiza a lista de instituições
          if (!this.formData.linkedInstitutions) {
            this.formData.linkedInstitutions = [];
          }
          this.formData.linkedInstitutions = [...this.formData.linkedInstitutions, institution];

          console.log('CredentialingPublic - Updated linkedInstitutions:', this.formData.linkedInstitutions);

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: 'Unidade adicionada com sucesso!',
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }).catch((error) => {
          console.error('Erro ao adicionar unidade:', error);
          this.$vs.notify({
            title: 'Erro',
            text: 'Erro ao adicionar unidade. Tente novamente.',
            color: 'danger',
            position: 'top-right'
          });
        });
      } catch (error) {
        console.error('Erro ao adicionar unidade:', error);
        this.$vs.notify({
          title: 'Erro',
          text: 'Erro inesperado ao adicionar unidade. Tente novamente.',
          color: 'danger',
          position: 'top-right'
        });
      }
    },

    updateLinkedInstitutions(data) {
      console.log('CredentialingPublic - updateLinkedInstitutions chamado com:', data);
      if (data && data.linked_institutions) {
        console.log('CredentialingPublic - linked_institutions recebido:', data.linked_institutions);
        this.formData.linkedInstitutions = Array.isArray(data.linked_institutions) ? data.linked_institutions : [];
        console.log('CredentialingPublic - linkedInstitutions atualizado:', this.formData.linkedInstitutions);
      } else {
        console.log('CredentialingPublic - dados inválidos recebidos em updateLinkedInstitutions');
      }
    },

    async handleRemoveInstitution(institution) {
      try {
        // Chama o serviço para remover a unidade
        await this.service.deleteLinkedInstitution(this.link, this.credentialingId, institution.institution_id).then(() => {
          // Remove da lista local apenas após sucesso da API
          const index = this.formData.linkedInstitutions.findIndex(i =>
            i.institution_id === institution.institution_id
          );
          if (index !== -1) {
            this.formData.linkedInstitutions.splice(index, 1);
          }

          // Notifica sucesso
          this.$vs.notify({
            title: 'Sucesso',
            text: 'Unidade removida com sucesso!',
            color: 'success',
            position: 'top-center',
            time: 4000,
            icon: 'feather icon-check-circle'
          });
        }).catch((error) => {
          console.error('Erro ao remover unidade:', error);
          this.$vs.notify({
            title: 'Erro',
            text: 'Erro ao remover unidade. Tente novamente.',
            color: 'danger',
            position: 'top-right'
          });
        });
      } catch (error) {
        console.error('Erro ao remover unidade:', error);
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.resetForm();
    },

    // ========================================
    // MÉTODOS DO BADGE FLUTUANTE
    // ========================================

    /**
     * Verifica se o badge deve ficar flutuante baseado na posição de scroll
     */
    checkFloatingBadgePosition() {
      if (!this.$refs.badgeContainer) return;

      const badgeContainer = this.$refs.badgeContainer;
      const rect = badgeContainer.getBoundingClientRect();
      const threshold = 50; // pixels de tolerância

      // Armazena a posição original ANTES de verificar se deve flutuar
      // Só armazena se ainda não foi armazenada E se o container está visível
      if (!this.originalBadgePosition && rect.width > 0 && rect.height > 0) {
        // Procura pela div que contém os badges (lado direito)
        const badgeDiv = badgeContainer.querySelector('.d-flex.align-items-center');
        let targetRect = rect;

        if (badgeDiv) {
          targetRect = badgeDiv.getBoundingClientRect();
          console.log('Badge div encontrada, usando sua posição:', targetRect);
        } else {
          console.log('Badge div não encontrada, usando container principal:', targetRect);
        }

        // Calcula a posição do lado direito (onde ficam os badges)
        const containerRight = targetRect.right;
        const rightPosition = window.innerWidth - containerRight;

        this.originalBadgePosition = {
          top: targetRect.top + window.scrollY,
          right: rightPosition,
          width: targetRect.width,
          containerRight: containerRight
        };

        console.log('Posição original capturada:', this.originalBadgePosition);
      }

      // Se o container dos badges saiu da tela (topo), ativa o floating
      this.floatingSavingBadge = rect.bottom < threshold;
    },

    /**
     * Obtém a cor de warning dinâmica
     */
    getWarningColor() {
      // 1. Prioridade: Cor customizada da API
      if (this.portalCebasConfig && this.portalCebasConfig.warning_color) {
        return this.portalCebasConfig.warning_color;
      }

      // 2. Tenta obter da variável CSS do sistema
      const root = getComputedStyle(document.documentElement);
      const possibleWarningVars = [
        '--bs-warning',
        '--warning',
        '--vs-warning',
        '--theme-warning',
        '--color-warning',
        '--dynamic-warning'
      ];

      for (const varName of possibleWarningVars) {
        const color = root.getPropertyValue(varName).trim();
        if (color && color !== '') {
          return color;
        }
      }

      // 3. Fallback: Cor padrão warning
      return '#ff9f43';
    },

    /**
     * Handler do evento de scroll
     */
    handleScroll() {
      this.checkFloatingBadgePosition();
    },

    /**
     * Handler do evento de resize
     */
    handleResize() {
      // Recalcula posições quando a janela é redimensionada
      this.originalBadgePosition = null;
      this.$nextTick(() => {
        this.checkFloatingBadgePosition();
      });
    },

    getFloatingBadgeStyle() {
      const baseStyle = {
        'background-color': this.getWarningColor() + ' !important',
        'border-color': this.getWarningColor() + ' !important',
        color: '#fff !important',
        'z-index': '1050',
        'backdrop-filter': 'blur(4px)',
        'box-shadow': '0 4px 6px rgba(0, 0, 0, 0.1)',
        animation: 'pulse 2s infinite'
      };

      if (!this.originalBadgePosition) {
        // Fallback: posiciona no canto superior direito da área de conteúdo
        return {
          ...baseStyle,
          position: 'fixed',
          top: '20px',
          right: '20px'
        };
      }

      // Usa a posição original calculada
      const rightPosition = this.originalBadgePosition.right;

      console.log('Aplicando estilo flutuante com right:', rightPosition);

      return {
        ...baseStyle,
        position: 'fixed',
        top: '20px',
        right: `${rightPosition}px`
      };
    },

    // Adicionar método para verificar se é um arquivo
    isFileObject(obj) {
      // Verificação mais robusta para detectar objetos File
      return obj && typeof obj === 'object' &&
             typeof obj.size === 'number' &&
             typeof obj.type === 'string' &&
             typeof obj.name === 'string' &&
             typeof obj.lastModified === 'number';
    },
  },
  mounted() {
    this.getCredentialingByLink();

    // Event listeners para o badge flutuante
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);

    // Verificação inicial da posição do badge com múltiplas tentativas
    this.$nextTick(() => {
      this.checkFloatingBadgePosition();

      // Tenta novamente após um pequeno delay para garantir que o DOM esteja totalmente renderizado
      setTimeout(() => {
        if (!this.originalBadgePosition) {
          console.log('Tentando capturar posição novamente após delay...');
          this.checkFloatingBadgePosition();
        }
      }, 100);

      // Última tentativa após mais tempo
      setTimeout(() => {
        if (!this.originalBadgePosition) {
          console.log('Tentativa final de capturar posição...');
          this.checkFloatingBadgePosition();
        }
      }, 500);
    });
  },
  beforeMount() {
    this.$vs.loading()
    const promisses = []

    // Service
    this.service = CebasCredentialingService.build(this.$vs)

    Promise.all(promisses).then(() => {
      this.$vs.loading.close()
    })
  },
  beforeDestroy() {
    // Remove event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
}
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/simple-content.scss';
@import '~@core/scss/base/bootstrap-extended/include';
@import 'src/assets/scss/accessibility.scss';

.bkg_img {
  position: fixed;
  scale: 1.2;
  right: 0;
  bottom: 0;
}

#portal-cebas-credentialing-public {
  @apply top-0 bottom-0 right-0 left-0;
  padding: 1% 1%;
  padding-top: 3rem !important;

  @screen sm {
    padding: 1% 5%;
  }

  @screen lg {
    padding: 3% 18%;
  }

  & > .card {
    min-height: 50vh !important;

    & ._row {
      width: 100%;
    }

    & > .card-header {
      padding: 0.5rem 1.5rem;
    }
  }

  & table.b-table {
    & th, td {
      padding: 1px;
      @screen sm {
        padding: 0.72rem 2rem;
      }
    }
  }

  & .card-footer {
    padding: 0.5rem;
    @screen sm {
      padding: 1.5rem;
    }
  }
}

// Estilos específicos para o wizard
.step-content {
  padding: 1rem 0;
}

// Classes dinâmicas para cores customizadas
.bg-dynamic-primary {
  background-color: var(--dynamic-primary) !important;
}

.text-dynamic-primary {
  color: var(--dynamic-primary) !important;
}

.border-dynamic-primary {
  border-color: var(--dynamic-primary) !important;
}

// Headers dinâmicos para cards
.header-bg-dynamic .card-header {
  background-color: var(--dynamic-primary) !important;
  border-bottom-color: var(--dynamic-primary) !important;
}

// Botões dinâmicos
.btn-dynamic-primary {
  background-color: var(--dynamic-primary) !important;
  border-color: var(--dynamic-primary) !important;
  color: #fff !important;

  &:hover, &:focus, &:active {
    background-color: var(--dynamic-primary-dark) !important;
    border-color: var(--dynamic-primary-dark) !important;
    color: #fff !important;
  }

  &:disabled {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
    opacity: 0.6;
  }
}

.btn-outline-dynamic-primary {
  background-color: transparent !important;
  border-color: var(--dynamic-primary) !important;
  color: var(--dynamic-primary) !important;

  &:hover, &:focus, &:active {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
    color: #fff !important;
  }

  &:disabled {
    border-color: var(--dynamic-primary) !important;
    color: var(--dynamic-primary) !important;
    opacity: 0.6;
  }
}

// Container dos checkboxes
.checkbox-wrapper {
  margin-bottom: 0;

  ::v-deep .form-group {
    margin-bottom: 0;
  }
}

// Estilo customizado para checkboxes grandes
.custom-checkbox-large {
  display: flex !important;
  align-items: flex-start !important;
  padding: 0.75rem;
  border: 1px solid #e3e6f0;
  border-radius: 0.5rem;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
  margin-bottom: 0;

  &:hover {
    background-color: #f1f3f4;
    border-color: #d1d3e2;
  }

  ::v-deep .custom-control-label {
    padding-left: 0.5rem;
    margin-bottom: 0;
    line-height: 1.4;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    position: relative;

    &::before {
      content: '';
      position: relative;
      top: 0.125rem;
      left: 0;
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.75rem;
      flex-shrink: 0;
      border: 2px solid #adb5bd;
      border-radius: 0.25rem;
      transition: all 0.2s ease;
      background-color: #fff;
      display: inline-block;
    }

    &::after {
      display: none !important;
      opacity: 0 !important;
    }
  }

  ::v-deep .custom-control-input {
    position: absolute;
    left: 0;
    z-index: -1;
    width: 1.25rem;
    height: 1.25rem;
    opacity: 0;

    &:checked ~ .custom-control-label {
      &::before {
        border-color: #28a745;
        background-color: #28a745;
        transform: scale(1.05);
      }

      &::after {
        opacity: 1;
        transform: scale(1);
      }
    }

    &:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }

    &:disabled ~ .custom-control-label {
      color: #6c757d;
      cursor: not-allowed;

      &::before {
        background-color: #e9ecef;
        border-color: #adb5bd;
      }

      &::after {
        opacity: 0;
      }
    }
  }
}

.checkbox-text {
  font-weight: 500;
  line-height: 1.5;
  text-align: justify;
  display: block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

// Checkboxes dinâmicos
.checkbox-dynamic-primary {
  &.custom-checkbox-large {
    ::v-deep .custom-control-input:checked ~ .custom-control-label {
      &::before {
        background-color: var(--dynamic-primary) !important;
        border-color: var(--dynamic-primary) !important;
      }

      &::after {
        opacity: 1 !important;
        transform: scale(1) !important;
      }
    }

    ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
    }
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }

  ::v-deep .form-check-input:checked {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep .form-check-input:focus {
    border-color: var(--dynamic-primary) !important;
    box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }
}

// Seção de verificação
.verification-section {
  .checkbox-wrapper {
    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    .invalid-feedback {
      margin-top: 0.5rem;
      margin-left: 2rem;
      font-size: 0.875rem;
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .custom-checkbox-large {
    padding: 0.5rem;

    ::v-deep .custom-control-label {
      padding-left: 0.25rem;

      &::before {
        margin-right: 0.5rem;
      }
    }
  }

  .checkbox-text {
    font-size: 0.9rem;
  }
}

// Modal dos termos com cores dinâmicas
::v-deep .terms-modal-custom {
  .modal-footer .btn-primary {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
    color: #fff !important;

    &:hover, &:focus, &:active {
      background-color: var(--dynamic-primary-dark, var(--dynamic-primary)) !important;
      border-color: var(--dynamic-primary-dark, var(--dynamic-primary)) !important;
      color: #fff !important;
      box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
    }

    &:not(:disabled):not(.disabled):active {
      background-color: var(--dynamic-primary-dark, var(--dynamic-primary)) !important;
      border-color: var(--dynamic-primary-dark, var(--dynamic-primary)) !important;
    }
  }
}

.floating-saving-badge {
  position: fixed !important;
  z-index: 1050;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
}

// Container principal com posição relativa
.main-content-container {
  position: relative;
}

// Animação de pulsação
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

// Animação de rotação para o ícone
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}

// Classes dinâmicas para warning color
.bg-dynamic-warning {
  background-color: var(--dynamic-warning) !important;
}

.text-dynamic-warning {
  color: var(--dynamic-warning) !important;
}

.border-dynamic-warning {
  border-color: var(--dynamic-warning) !important;
}

// NOVA ABORDAGEM - ÍCONE DE CHECK COM BACKGROUND-IMAGE
.custom-checkbox-large {
  // Remove qualquer ::after duplicado
  ::v-deep .custom-control-label::after {
    display: none !important;
    content: none !important;
    opacity: 0 !important;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='white' stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z'/%3e%3c/svg%3e") !important;
    background-size: 0.75rem 0.75rem !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
}

// Para cores dinâmicas
.checkbox-dynamic-primary.custom-checkbox-large {
  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='white' stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z'/%3e%3c/svg%3e") !important;
    background-size: 0.75rem 0.75rem !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
}

.checkbox-text {
  font-weight: 500;
  line-height: 1.5;
  text-align: justify;
  display: block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}
</style>
